import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import api from '../lib/api';
import { fmtDate, fmtDateTime, fmtMonYY } from '../lib/date';
import { log } from '../lib/logger';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import MonthlyTablesSection from '../components/MonthlyTablesSection';
import EvalToSessionChart from '../components/EvalToSessionChart';
import DirectorMonthlyReview from '../components/DirectorMonthlyReview';
import { CptTrendChart } from '../components/bi/CptTrendChart';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  RadialBarChart, RadialBar, ComposedChart, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine,
  LabelList,
} from 'recharts';
import {
  DollarSign, Target, AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight,
  Building2, Users, RefreshCcw, UploadCloud, Sparkles, Activity, Trophy, ShieldAlert, Wallet, LineChart as LineIcon,
  PieChart as PieIcon, Crown, ChevronRight, Gauge, Award, Zap, Inbox, TrendingUp, CalendarCheck,
} from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';

// ─── Palette — dark royal + gold + soft lavender + muted rose ───
const C = {
  bg:        '#685B72',      // Deep ink
  bg2:       '#685B72',
  panel:     '#FFFFFF',
  paper:     '#FBF8F4',
  ink:       '#2D2833',
  ink2:      '#685B72',
  soft:      '#685B72',
  border:    '#E9E3DA',
  hair:      '#B08A3A',
  gold:      '#B08A3A',
  gold2:     '#B08A3A',
  purple:    '#685B72',
  lavender:  '#685B72',
  teal:      '#839E97',   /* brand Sage — success/on-target */
  rose:      '#8A3A3A',   /* muted brand-warm red — at risk / danger */
  amber:     '#B08A3A',   /* muted brand-warm amber — warning */
};

const CURRENCY = (n, digits = 0) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits})}`);
const CURRENCY_SHORT = (n) => {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n/1_000_000).toFixed(2)}M`;
  if (abs >= 10_000)     return `$${(n/1_000).toFixed(0)}k`;
  if (abs >= 1_000)      return `$${(n/1_000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};
const PCT = (n, d = 1) => (n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`);
const NUM = (n) => (n == null ? '—' : Number(n).toLocaleString());

// ─── Small building blocks ───
function Delta({ value, invert }) {
  if (value == null) return null;
  const pos = invert ? value < 0 : value > 0;
  const color = value === 0 ? C.soft : pos ? C.teal : C.rose;
  const Icon = value > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color }}>
      <Icon size={12} strokeWidth={2.4} />{Math.abs(value * 100).toFixed(1)}%
    </span>
  );
}

function Sparkline({ data, dataKey = 'collected', color = C.gold, height = 40 }) {
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
        <defs>
          <linearGradient id={`grad-${dataKey}-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.75} fill={`url(#grad-${dataKey}-${color.replace('#','')})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function StatTile({ label, value, sub, delta, invertDelta, tone, icon: Icon, spark, testid }) {
  return (
    <Card data-testid={testid} className="p-5 rounded-2xl border shadow-[0_1px_2px_rgba(31,26,44,0.04)] relative overflow-hidden bg-white" style={{ borderColor: C.border }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: C.soft }}>{label}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-serif text-[28px] leading-none" style={{ color: C.ink }}>{value}</div>
            {delta != null && <Delta value={delta} invert={invertDelta} />}
          </div>
          {sub && <div className="text-[11px] mt-2" style={{ color: C.soft }}>{sub}</div>}
        </div>
        {Icon && (
          <div className="rounded-xl p-2 shrink-0" style={{ background: `${tone || C.purple}15` }}>
            <Icon size={16} strokeWidth={1.7} style={{ color: tone || C.purple }} />
          </div>
        )}
      </div>
      {spark && <div className="mt-3 -mx-2"><Sparkline data={spark.data} dataKey={spark.key} color={tone || C.gold} height={34} /></div>}
    </Card>
  );
}

function InsightPill({ kind, headline, detail }) {
  const map = {
    success: { bg: 'linear-gradient(135deg, rgba(75,139,127,0.10), rgba(75,139,127,0.02))', border: 'rgba(75,139,127,0.35)', icon: CheckCircle2, color: C.teal, label: 'STRENGTH' },
    warn:    { bg: 'linear-gradient(135deg, rgba(216,154,74,0.10), rgba(216,154,74,0.02))',  border: 'rgba(216,154,74,0.35)',  icon: AlertTriangle,  color: C.amber, label: 'ATTENTION' },
    danger:  { bg: 'linear-gradient(135deg, rgba(176,72,63,0.10), rgba(176,72,63,0.02))',    border: 'rgba(176,72,63,0.35)',   icon: ShieldAlert,    color: C.rose,  label: 'ACTION' },
  };
  const s = map[kind] || map.warn;
  const Icon = s.icon;
  return (
    <div data-testid="bi-insight-card" className="rounded-2xl p-4 border relative overflow-hidden" style={{ background: s.bg, borderColor: s.border }}>
      <div className="flex items-start gap-3">
        <div className="rounded-lg p-1.5 shrink-0" style={{ background: `${s.color}22` }}>
          <Icon size={13} strokeWidth={2} style={{ color: s.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: s.color }}>{s.label}</div>
          <div className="text-[13px] font-serif mt-0.5 leading-tight" style={{ color: C.ink }}>{headline}</div>
          <div className="text-[11px] mt-1" style={{ color: C.ink2 }}>{detail}</div>
        </div>
      </div>
    </div>
  );
}

function RadialGauge({ value, label, sub, color, target }) {
  const raw = Number(value) || 0;
  const pct = Math.max(0, Math.min(1, raw));
  const displayPct = raw > 1 ? (raw * 100).toFixed(1) : (pct * 100).toFixed(1);
  const data = [{ name: 'value', value: pct * 100, fill: color }];
  return (
    <div className="relative flex items-center justify-center" style={{ width: 140, height: 140 }}>
      <ResponsiveContainer width={140} height={140}>
        <RadialBarChart cx="50%" cy="50%" innerRadius={48} outerRadius={66} barSize={11} data={data} startAngle={90} endAngle={-270}>
          <RadialBar background={{ fill: '#B08A3A' }} dataKey="value" cornerRadius={10} fill={color} isAnimationActive={false} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-2">
        <div className="font-serif leading-none tabular-nums" style={{ color: C.ink, fontSize: 22 }}>{displayPct}%</div>
        <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: C.soft }}>{label}</div>
        {sub && <div className="text-[10px] mt-0.5 text-center" style={{ color: C.soft }}>{sub}</div>}
        {target != null && <div className="text-[9px] mt-0.5" style={{ color: C.gold }}>target {(target*100).toFixed(1)}%</div>}
      </div>
    </div>
  );
}

// Half-doughnut speedometer — car-dashboard style with tri-color arc + needle. Used for hero-level vitals.
function SpeedometerGauge({ value, label, sub, color, target, testid }) {
  const raw = Number(value) || 0;
  const pct = Math.max(0, Math.min(1, raw));
  const displayPct = raw > 1 ? (raw * 100).toFixed(1) : (pct * 100).toFixed(1);

  // ── Geometry ──────────────────────────────────────────────────────────
  // Half-circle gauge: sweep 180° → 360° (left→top→right). We work in
  // standard math radians where 180° = left edge, 270° = top, 360° = right.
  const W = 240, H = 150;
  const CX = W / 2, CY = H - 20;
  const R  = 82;            // arc mid-radius (stroke centered on this)
  const SW = 22;            // arc stroke width
  const RED   = '#8A3A3A';
  const AMBER = '#B08A3A';
  const GREEN = '#3F7D6E';
  // Map 0..1 → π..2π (left→right sweeping over the top half).
  const toRad = (p) => Math.PI + Math.max(0, Math.min(1, p)) * Math.PI;
  const pointAt = (p, r = R) => {
    const a = toRad(p);
    return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
  };
  // Every band on a half-gauge is ≤ 180° wide → large-arc-flag ALWAYS 0.
  const arc = (p1, p2) => {
    const [x1, y1] = pointAt(p1);
    const [x2, y2] = pointAt(p2);
    return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`;
  };
  // Zone boundaries — target defines the green cutoff. Amber sits 20 points below target; red below that.
  const t = target != null ? Math.max(0.4, Math.min(0.99, target)) : 0.9;
  const amberStart = Math.max(0.25, t - 0.2);

  // ── Needle ─────────────────────────────────────────────────────────────
  const needleAng = toRad(pct);
  const needleTipR = R + SW / 2 - 4;
  const needleBaseR = 6;
  const tipX = CX + needleTipR * Math.cos(needleAng);
  const tipY = CY + needleTipR * Math.sin(needleAng);
  const perpL = needleAng + Math.PI / 2;
  const perpR = needleAng - Math.PI / 2;
  const lbX = CX + needleBaseR * Math.cos(perpL);
  const lbY = CY + needleBaseR * Math.sin(perpL);
  const rbX = CX + needleBaseR * Math.cos(perpR);
  const rbY = CY + needleBaseR * Math.sin(perpR);
  const zoneColor = pct >= t ? GREEN : pct >= amberStart ? AMBER : RED;

  // ── Tick marks + scale labels ─────────────────────────────────────────
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const p = i / 10;
    const [ox, oy] = pointAt(p, R + SW / 2 + 4);
    const [ix, iy] = pointAt(p, R + SW / 2 - 2);
    const major = i % 2 === 0;   // major every 20%
    // label position sits outside the ticks — pushed a little farther for majors
    const [lx, ly] = pointAt(p, R + SW / 2 + (major ? 15 : 10));
    ticks.push({ ox, oy, ix, iy, major, label: major ? String(i * 10) : '', lx, ly });
  }
  // Amber-band start + target labels — sit under the arc so they read as a legend rather than fighting with the value scale.
  const [amx, amy] = pointAt(amberStart, R - SW / 2 - 12);
  const [tgx, tgy] = pointAt(t, R - SW / 2 - 12);

  return (
    <div className="relative flex flex-col items-center" style={{ width: W }} data-testid={testid}>
      <svg width={W} height={H + 12} viewBox={`0 0 ${W} ${H + 12}`} style={{ overflow: 'visible' }}>
        {/* Background track — very light so the color bands sit crisply on top */}
        <path d={arc(0, 1)} stroke="#F1EDF5" strokeWidth={SW} fill="none" strokeLinecap="butt" />
        {/* Red zone: 0 → amberStart */}
        <path d={arc(0, amberStart)} stroke={RED}   strokeWidth={SW} fill="none" strokeLinecap="butt" />
        {/* Amber zone: amberStart → target */}
        <path d={arc(amberStart, t)} stroke={AMBER} strokeWidth={SW} fill="none" strokeLinecap="butt" />
        {/* Green zone: target → 1.0 */}
        <path d={arc(t, 1)}          stroke={GREEN} strokeWidth={SW} fill="none" strokeLinecap="butt" />
        {/* Tick marks */}
        {ticks.map((tk, i) => (
          <line key={`tk-${i}`} x1={tk.ix} y1={tk.iy} x2={tk.ox} y2={tk.oy}
                stroke="#FFFFFF" strokeWidth={tk.major ? 2 : 1} opacity="0.9" />
        ))}
        {/* Major-tick scale labels — 0 / 20 / 40 / 60 / 80 / 100 */}
        {ticks.filter(tk => tk.major).map((tk, i) => (
          <text key={`lb-${i}`} x={tk.lx} y={tk.ly + 3}
                textAnchor="middle" fontSize="9" fill={C.soft}
                style={{ fontWeight: 600, letterSpacing: '0.02em' }}>
            {tk.label}
          </text>
        ))}
        {/* Zone-boundary callouts — shown UNDER the arc so users can read where each color begins */}
        <text x={amx} y={amy} textAnchor="middle" fontSize="8.5" fill={AMBER} style={{ fontWeight: 700 }}>
          {Math.round(amberStart * 100)}%
        </text>
        {target != null && (
          <text x={tgx} y={tgy} textAnchor="middle" fontSize="8.5" fill={GREEN} style={{ fontWeight: 700 }}>
            {Math.round(t * 100)}%
          </text>
        )}
        {/* Needle */}
        <polygon points={`${lbX},${lbY} ${rbX},${rbY} ${tipX},${tipY}`}
                 fill={C.ink} stroke={C.ink} strokeWidth="1" strokeLinejoin="round" />
        {/* Pivot cap */}
        <circle cx={CX} cy={CY} r={10} fill={C.ink} />
        <circle cx={CX} cy={CY} r={4}  fill={C.gold} />
      </svg>
      {/* Compact color-band legend so the meter is self-explanatory */}
      <div className="mt-1 flex items-center gap-2 text-[9px]" style={{ color: C.soft }} data-testid={`${testid}-legend`}>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: RED }} /> 0–{Math.round(amberStart*100)}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: AMBER }} /> {Math.round(amberStart*100)}–{Math.round(t*100)}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: GREEN }} /> {Math.round(t*100)}+</span>
      </div>
      <div className="mt-1 flex flex-col items-center leading-tight">
        <div className="font-serif tabular-nums" style={{ color: zoneColor, fontSize: 30, lineHeight: 1 }}>
          {displayPct}<span className="text-[16px]" style={{ color: C.soft }}>%</span>
        </div>
        <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: C.soft, fontWeight: 700 }}>{label}</div>
        {sub && <div className="text-[10px] mt-0.5 text-center" style={{ color: C.soft }}>{sub}</div>}
        {target != null && <div className="text-[9px] mt-0.5" style={{ color: C.gold }}>target {(target*100).toFixed(0)}%</div>}
      </div>
    </div>
  );
}

function SectionTitle({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <h3 className="font-serif text-xl leading-none" style={{ color: C.ink }}>{title}</h3>
        {subtitle && <div className="text-[11px] mt-1.5" style={{ color: C.soft }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

function Tab({ id, active, label, icon: Icon, onClick }) {
  return (
    <button
      data-testid={`bi-tab-${id}`}
      onClick={() => onClick(id)}
      className="relative inline-flex items-center gap-2 px-4 h-11 rounded-full text-[13px] font-medium transition-all"
      style={{
        color: active ? C.ink : C.soft,
        background: active ? '#FFF' : 'transparent',
        boxShadow: active ? '0 1px 2px rgba(31,26,44,0.06), 0 0 0 1px rgba(200,190,175,0.5)' : 'none',
      }}
    >
      <Icon size={14} strokeWidth={1.75} /> {label}
    </button>
  );
}

// ─── Global Utilization Speedometer — business-day-prorated group meter ───
function GlobalUtilizationSpeedometer({ u }) {
  if (!u) return null;
  const gp = Number(u.group_utilization || 0);
  const target = u.min_expected_pct || 0.90;
  const color = gp >= target ? C.teal : gp >= 0.70 ? C.amber : C.rose;
  const status = gp >= target ? 'On pace' : gp >= 0.70 ? 'Below target' : 'Well below target';
  const bd = u.biz_days_elapsed;
  const bdTotal = u.biz_days_in_month;
  const paceTarget = u.group_elapsed_target;
  const paceTargetFull = u.group_target;
  const data = [{ name: 'value', value: Math.min(100, Math.max(0, gp * 100)), fill: color }];
  return (
    <Card
      className="p-6 rounded-3xl mb-4 bg-white border relative overflow-hidden"
      style={{ borderColor: C.border, background: `linear-gradient(135deg, ${color}08 0%, #FFFFFF 60%)` }}
      data-testid="bi-global-utilization-speedometer"
    >
      <div className="flex flex-wrap items-center gap-6 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: color }}>
            <Gauge size={12} strokeWidth={2.4} /> Global Utilization · {u.month} {u.year}
          </div>
          <div className="mt-2 text-[13px]" style={{ color: C.soft }}>
            Business-day prorated · <span style={{ color: C.ink, fontWeight: 700 }}>{bd ?? '—'}</span> of {bdTotal ?? '—'} biz days elapsed
          </div>
          <div className="text-[12px] mt-1" style={{ color: C.soft }}>
            {NUM(u.group_shows)} shows · pace target <span style={{ color: C.ink, fontWeight: 700 }}>{NUM(paceTarget)}</span>
            {paceTargetFull ? <span> · full-month target {NUM(paceTargetFull)}</span> : null}
          </div>
          <div className="text-[11px] mt-3 flex flex-wrap gap-3" style={{ color: C.soft }}>
            {u.group_mom_delta != null && (
              <span style={{ color: u.group_mom_delta >= 0 ? C.teal : C.rose, fontWeight: 700 }}>
                {u.group_mom_delta >= 0 ? '▲' : '▼'} {Math.abs(u.group_mom_delta*100).toFixed(1)}% MoM
              </span>
            )}
            {u.group_yoy_delta != null && (
              <span style={{ color: u.group_yoy_delta >= 0 ? C.teal : C.rose, fontWeight: 700 }}>
                {u.group_yoy_delta >= 0 ? '▲' : '▼'} {Math.abs(u.group_yoy_delta*100).toFixed(1)}% YoY
              </span>
            )}
          </div>
        </div>
        <div className="relative flex items-center justify-center" style={{ width: 220, height: 160 }}>
          <ResponsiveContainer width={220} height={160}>
            <RadialBarChart cx="50%" cy="90%" innerRadius={70} outerRadius={110} barSize={18} data={data} startAngle={180} endAngle={0}>
              <RadialBar background={{ fill: '#F1EDF5' }} dataKey="value" cornerRadius={12} fill={color} isAnimationActive={false} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none">
            <div className="font-serif leading-none tabular-nums" style={{ color: color, fontSize: 42 }} data-testid="bi-global-utilization-value">
              {(gp * 100).toFixed(1)}<span className="text-[20px]" style={{ color: C.soft }}>%</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: color, fontWeight: 700 }}>{status}</div>
            <div className="text-[10px] mt-0.5" style={{ color: C.gold }}>target {(target * 100).toFixed(0)}%</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Utilization tile — group meter + per-clinic bars ───
function UtilizationCard({ u }) {
  if (!u) return null;
  const gp = u.group_utilization || 0;
  const target = u.min_expected_pct || 0.90;
  const gpColor = gp >= target ? C.teal : gp >= 0.70 ? C.amber : C.rose;
  const barData = (u.clinics || []).map(c => ({
    ...c, pct: Math.min(1.15, c.utilization || 0),
    color: c.utilization >= target ? C.teal : c.utilization >= 0.70 ? C.amber : C.rose,
  }));
  return (
    <Card data-testid="bi-utilization-card" className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.gold }}>
            <Gauge size={12} strokeWidth={2.4} /> Utilization · {u.month} {u.year}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
            {u.monthly_target_per_clinic} shows/month = 100% · target ≥{(target*100).toFixed(1)}%
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif tabular-nums leading-none" style={{ color: gpColor, fontSize: 34 }}>
            {(gp * 100).toFixed(1)}%
          </div>
          <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: C.soft }}>Group</div>
          <div className="text-[11px] mt-1 flex items-center justify-end gap-2" style={{ color: C.soft }}>
            {u.group_mom_delta != null && (
              <span className={u.group_mom_delta >= 0 ? 'text-teal-600' : 'text-rose-600'}>
                {u.group_mom_delta >= 0 ? '▲' : '▼'} {Math.abs(u.group_mom_delta*100).toFixed(1)}% MoM
              </span>
            )}
            {u.group_yoy_delta != null && (
              <span className={u.group_yoy_delta >= 0 ? 'text-teal-600' : 'text-rose-600'}>
                {u.group_yoy_delta >= 0 ? '▲' : '▼'} {Math.abs(u.group_yoy_delta*100).toFixed(1)}% YoY
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="text-[11px] mb-2" style={{ color: C.soft }}>
        {u.group_shows.toLocaleString()} of {u.group_target.toLocaleString()} target shows this month
      </div>
      <div className="space-y-2">
        {barData.map((c) => (
          <div key={c.clinic} data-testid={`bi-util-row-${c.clinic.replace(/\s+/g,'-').toLowerCase()}`} className="flex items-center gap-3">
            <div className="w-24 text-[12px] shrink-0 truncate" style={{ color: C.ink }}>{c.clinic}</div>
            <div className="flex-1 relative h-3 rounded-full overflow-hidden" style={{ background: '#F5F1EA', border: `1px solid ${C.border}` }}>
              {/* Target line at 90% */}
              <div className="absolute top-0 bottom-0" style={{ left: `${target*100}%`, width: 1, background: C.ink2, opacity: 0.35 }} />
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, c.pct*100)}%`, background: c.color }} />
            </div>
            <div className="w-14 text-right tabular-nums text-[12px] font-semibold" style={{ color: c.color }}>
              {(c.utilization*100).toFixed(1)}%
            </div>
            <div className="w-12 text-right tabular-nums text-[11px]" style={{ color: C.soft }}>
              {c.shows}
            </div>
            <div className="w-16 text-right text-[10px]" style={{ color: c.mom_delta != null ? (c.mom_delta >= 0 ? C.teal : C.rose) : C.soft }}>
              {c.mom_delta != null ? `${c.mom_delta >= 0 ? '↑' : '↓'}${Math.abs(c.mom_delta*100).toFixed(1)}% MoM` : '—'}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── OD Quarterly Bonus meter ───
function BonusMeterCard({ b }) {
  if (!b) return null;
  // Iter 242 — use the business-day-prorated pace target so mid-quarter
  // reporting isn't skewed by the full-quarter (2,225) denominator.
  const paceTargetPer = b.elapsed_target_per_clinic || b.target_per_clinic;
  const rows = (b.clinics || []).map(c => {
    const paceProg = (c.pace_progress != null) ? c.pace_progress : (c.progress || 0);
    // Color: green ≥ 100% of pace, amber 90–99%, red below.
    const color = paceProg >= 1.0 ? C.teal : paceProg >= 0.90 ? C.amber : C.rose;
    return { ...c, paceProg, pct: Math.min(1.15, paceProg), color };
  });
  const groupPace = paceTargetPer * b.total_clinics
    ? (b.group_shows || 0) / (b.group_elapsed_target || (paceTargetPer * b.total_clinics))
    : 0;
  const bdElapsed = b.business_days_elapsed;
  const bdTotal = b.business_days_total;
  return (
    <Card data-testid="bi-bonus-card" className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.gold }}>
            <Award size={12} strokeWidth={2.4} /> OD Bonus · {b.label}
          </div>
          <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
            Pace target <span style={{ fontWeight: 700, color: C.ink2 }}>{NUM(paceTargetPer)}</span>/clinic
            {bdElapsed != null && bdTotal ? <> · {bdElapsed}/{bdTotal} biz days elapsed</> : null}
            {' · '}full-quarter {NUM(b.target_per_clinic)} · {(b.qualifying_pct*100).toFixed(1)}%+ qualifies · {b.months.join(' · ')}
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif tabular-nums leading-none" style={{ color: b.qualifying_count > 0 ? C.teal : C.rose, fontSize: 34 }} data-testid="bi-bonus-qualifying-count">
            {b.qualifying_count}<span className="text-[18px]" style={{ color: C.soft }}>/{b.total_clinics}</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: C.soft }}>Bonus track</div>
          {b.on_pace_count != null && (
            <div className="text-[10px] mt-1" style={{ color: C.soft }}>
              <span style={{ fontWeight: 700, color: groupPace >= 1 ? C.teal : groupPace >= 0.9 ? C.amber : C.rose }}>{b.on_pace_count}/{b.total_clinics}</span> on pace (≥90% of pace target)
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.clinic} data-testid={`bi-bonus-row-${c.clinic.replace(/\s+/g,'-').toLowerCase()}`} className="flex items-center gap-3">
            <div className="w-24 text-[12px] shrink-0 truncate flex items-center gap-1" style={{ color: C.ink }}>
              {c.qualifies && <Zap size={11} style={{ color: C.gold }} strokeWidth={2.4} />}
              {c.clinic}
            </div>
            <div className="flex-1 relative h-3 rounded-full overflow-hidden" style={{ background: '#F5F1EA', border: `1px solid ${C.border}` }}>
              <div className="absolute top-0 bottom-0" style={{ left: `${(b.qualifying_pct||0.9)*100}%`, width: 1, background: C.ink2, opacity: 0.35 }} />
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, c.pct*100)}%`, background: c.color }} />
            </div>
            <div className="w-14 text-right tabular-nums text-[12px] font-semibold" style={{ color: c.color }}>
              {(c.paceProg*100).toFixed(1)}%
            </div>
            <div className="w-20 text-right tabular-nums text-[11px]" style={{ color: C.soft }}>
              {c.shows.toLocaleString()} / {NUM(c.elapsed_target || paceTargetPer)}
            </div>
            <div className="w-24 text-right text-[10px]" style={{ color: c.pace_status === 'on_pace' ? C.teal : c.pace_status === 'close' ? C.amber : C.rose }}>
              {c.pace_status === 'on_pace' ? 'On pace' : c.pace_status === 'close' ? `${c.pace_gap || 0} behind pace` : c.pace_status === 'in_progress' ? 'Too early' : `${c.pace_gap || 0} behind`}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── KPI trend rows — YTD and Last-3-completed-months with directional arrows ───
// Data cutoff for BI is Jan 2026, so "YTD" = whatever appears in the current
// series. The prior-period comparator for YTD is intentionally hidden (there
// is no 2025 baseline available). Last-3-months compares against the 3
// months preceding those, if we have enough history.
function KpiTrendRow({ d }) {
  const income = d.income_by_month || [];      // [{month, gross, net}]
  const coll   = d.collected_by_month || [];   // [{month, collected}]
  const lost   = d.lost_by_month || [];        // [{month, lost}]
  const rev    = d.revenue_by_month || [];     // [{month, avg_per_session, sessions, collected}]
  // Rev/session for YTD & trailing — compute from monthly aggregates.
  const _sum = (arr, key) => arr.reduce((s, x) => s + (Number(x[key]) || 0), 0);
  // "Last 3 completed months" = everything except the current (last) entry,
  // taking the 3 slots before that. Prior comparator = the 3 slots before those.
  const last3End = Math.max(0, income.length - 1);
  const last3 = income.slice(Math.max(0, last3End - 3), last3End);
  const prior3 = income.slice(Math.max(0, last3End - 6), Math.max(0, last3End - 3));
  const idxs = (arr) => arr.slice(Math.max(0, last3End - 3), last3End);
  const idxsPrior = (arr) => arr.slice(Math.max(0, last3End - 6), Math.max(0, last3End - 3));

  const ytd = {
    gross:  _sum(income, 'gross'),
    coll:   _sum(coll,   'collected'),
    lost:   _sum(lost,   'lost'),
    sess:   _sum(rev,    'sessions'),
    revSum: _sum(rev,    'collected'),
  };
  const t3 = {
    gross:  _sum(last3, 'gross'),
    coll:   _sum(idxs(coll), 'collected'),
    lost:   _sum(idxs(lost), 'lost'),
    sess:   _sum(idxs(rev),  'sessions'),
    revSum: _sum(idxs(rev),  'collected'),
  };
  const p3 = {
    gross:  _sum(prior3, 'gross'),
    coll:   _sum(idxsPrior(coll), 'collected'),
    lost:   _sum(idxsPrior(lost), 'lost'),
    sess:   _sum(idxsPrior(rev),  'sessions'),
    revSum: _sum(idxsPrior(rev),  'collected'),
  };
  const dlt = (cur, prior) => (prior ? (cur - prior) / prior : null);
  const rpS  = (bucket) => (bucket.sess ? bucket.revSum / bucket.sess : 0);
  const t3Deltas = {
    gross: dlt(t3.gross,  p3.gross),
    coll:  dlt(t3.coll,   p3.coll),
    lost:  dlt(t3.lost,   p3.lost),
    rev:   dlt(rpS(t3),   rpS(p3)),
  };

  const Row = ({ testid, label, vals, deltas }) => (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid={testid}>
      <MiniStat label={`Gross · ${label}`} value={CURRENCY_SHORT(vals.gross)} delta={deltas?.gross} />
      <MiniStat label={`Collected · ${label}`} value={CURRENCY_SHORT(vals.coll)} delta={deltas?.coll} />
      <MiniStat label={`Lost · ${label}`} value={CURRENCY_SHORT(vals.lost)} delta={deltas?.lost} invert />
      <MiniStat label={`Rev/Session · ${label}`} value={CURRENCY(rpS(vals))} delta={deltas?.rev} />
      <MiniStat label={`Sessions · ${label}`} value={NUM(vals.sess)} />
    </div>
  );

  return (
    <div className="space-y-2 mb-2">
      <Row testid="bi-kpi-t3-row"  label="Last 3 mo" vals={t3}  deltas={t3Deltas} />
    </div>
  );
}

function MiniStat({ label, value, delta, invert }) {
  return (
    <div className="rounded-xl border px-3 py-2 bg-white flex items-center justify-between gap-2" style={{ borderColor: C.border }}>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest font-semibold truncate" style={{ color: C.soft }}>{label}</div>
        <div className="font-serif text-[15px] tabular-nums mt-0.5" style={{ color: C.ink }}>{value}</div>
      </div>
      {delta != null && <Delta value={delta} invert={invert} />}
    </div>
  );
}

// ─── Overview tab ───
function OverviewTab({ d }) {
  const k = d.kpis || {};
  const spark = d.collected_by_month || [];
  const insights = d.insights || [];
  // Payroll margin tiles — pulled straight from the latest finalized
  // payroll period so Income/Session + Payroll/Session sit alongside
  // the rest of the BI without the executive tab-hopping.
  const [payrollTrend, setPayrollTrend] = React.useState({ current: null, history: [] });
  React.useEffect(() => {
    Promise.all([
      api.get('/payroll/kpi/current').catch(() => ({ data: { current: null } })),
      api.get('/payroll/kpi/history', { params: { limit: 24 } }).catch(() => ({ data: { history: [] } })),
    ]).then(([cur, hist]) => setPayrollTrend({ current: cur.data.current, history: hist.data.history || [] }));
  }, []);
  // ONE consistent field-preference helper — used across value, sparkline, latest, and 3-mo comparator.
  // Prior bug: value tile used `payroll_per_session_cents` first but sparkline + prior-avg used
  // `payroll_per_show_unit_cents` first, so displayed value and delta could diverge (e.g. tile
  // showed −4.5% against a 3-mo-avg tooltip of $57 while the true delta was ~−57.9%). Forcing a
  // single preference order eliminates the mismatch.
  const _incCents = (h) => Number(h?.income_per_session_cents  ?? h?.income_per_show_unit_cents  ?? h?.income_per_unit_cents  ?? 0);
  const _payCents = (h) => Number(h?.payroll_per_session_cents ?? h?.payroll_per_show_unit_cents ?? h?.payroll_per_unit_cents ?? 0);
  const _payrollSparkIncome = payrollTrend.history.map(h => ({
    period: (h.period_end || '').slice(5),
    income_per_session:  _incCents(h) / 100,
    payroll_per_session: _payCents(h) / 100,
    margin_per_session:  (_incCents(h) - _payCents(h)) / 100,
  }));
  const _latest = payrollTrend.current?.summary || {};
  const _incPerSess  = _incCents(_latest) / 100;
  const _payPerSess  = _payCents(_latest) / 100;
  const _marginPerSess = _incPerSess - _payPerSess;
  // Compare current against the avg of the 3 periods that came BEFORE
  // this one — that is the "improving?" question the owner asked. For
  // income and margin, up is good; for payroll, down is good — pass
  // invertDelta on the payroll tile.
  const _priorThree = _payrollSparkIncome.slice(-4, -1);   // 3 periods before latest
  const _avg = (arr, key) => arr.length ? arr.reduce((a, x) => a + (x[key] || 0), 0) / arr.length : 0;
  const _avgInc  = _avg(_priorThree, 'income_per_session');
  const _avgPay  = _avg(_priorThree, 'payroll_per_session');
  const _avgMar  = _avg(_priorThree, 'margin_per_session');
  const _incDelta = _avgInc  ? (_incPerSess    - _avgInc) / _avgInc  : null;
  const _payDelta = _avgPay  ? (_payPerSess    - _avgPay) / _avgPay  : null;
  const _marDelta = _avgMar  ? (_marginPerSess - _avgMar) / _avgMar  : null;
  const _money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <>
      {/* HERO — one massive number (full-width; speedometers moved below the momentum chart) */}
      <div className="grid grid-cols-1 gap-5 mb-6">
        <Card className="p-8 rounded-3xl relative overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${C.bg} 0%, #685B72 55%, #685B72 100%)`, borderColor: 'transparent' }}>
          <div className="absolute inset-0 opacity-[0.08]" style={{ background: `radial-gradient(circle at 15% 20%, ${C.gold} 0%, transparent 45%), radial-gradient(circle at 85% 65%, ${C.lavender} 0%, transparent 45%)` }} />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: C.gold }} title="Gross Income (receipts recorded in Income & Fees) minus processing fees. This is a receipt-based figure — it is not operating profit and does not account for payroll, rent, or other operating costs.">
              <Crown size={12} /> Collected Less Fees · YTD
            </div>
            <div className="mt-3 flex items-baseline gap-4 flex-wrap">
              <div className="font-serif text-white leading-none" style={{ fontSize: 'clamp(48px, 6vw, 76px)' }} data-testid="bi-hero-collected-less-fees">{CURRENCY(k.net_income_ytd)}</div>
              {k.net_mom_delta != null && (
                <div className={`text-[15px] font-semibold flex items-center gap-1 ${k.net_mom_delta >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} title="Current month vs prior month, with the prior month pro-rated to the same number of business days elapsed. Suppressed until at least 5 business days into the current month.">
                  {k.net_mom_delta >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                  {(Math.abs(k.net_mom_delta) * 100).toFixed(1)}% vs prior month (pace-matched)
                </div>
              )}
            </div>
            <div className="mt-4 text-[13px] max-w-lg" style={{ color: '#D4CADB' }}>
              Gross Income <span className="text-white font-semibold">{CURRENCY(k.gross_income_ytd)}</span> less <span className="text-white font-semibold">${Math.abs(k.fees_ytd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> processing fees. Separately, cash tied to service dates (DOS) collected <span className="text-white font-semibold">{CURRENCY(k.collected_dos)}</span>. Receipt‑based, not operating profit.
            </div>
            <div className="mt-6 h-16">
              <ResponsiveContainer width="100%" height={64}>
                <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.gold} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={C.gold} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="collected" stroke={C.gold} strokeWidth={2} fill="url(#heroGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-[10px] mt-1 uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {(d.months || []).map(m => <span key={m}>{m}</span>)}
            </div>
          </div>
        </Card>
      </div>

      {/* UTILIZATION + OD BONUS METERS */}
      {(d.utilization || d.od_bonus) && (
        <>
          <SectionTitle title="Utilization & OD bonus" subtitle="Monthly target set per clinic in Setup (default 720 shows/mo, prorated by business days elapsed and by data-through-date) · 2,225/quarter = bonus target (90%+ qualifies)" />
          <div className="grid xl:grid-cols-2 gap-4 mb-8">
            {d.utilization && <UtilizationCard u={d.utilization} />}
            {d.od_bonus     && <BonusMeterCard  b={d.od_bonus} />}
          </div>
        </>
      )}

      {/* KPI GRID — the strategic set */}
      <SectionTitle title="At-a-glance KPIs" subtitle="Headline YTD · Last 3 completed months trend below" />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-2">
        <StatTile testid="bi-kpi-gross-income-ytd" label="Gross Income YTD" value={CURRENCY_SHORT(k.gross_income_ytd)} delta={k.gross_mom_delta} sub="Payments received" icon={DollarSign} tone={C.gold}
                  spark={{ data: d.income_by_month, key: 'gross' }} />
        <StatTile testid="bi-kpi-collected-dos" label="Collected (DOS)" value={CURRENCY_SHORT(k.collected_dos)} delta={d.collected_maturing ? null : k.collected_mom_delta} sub={d.collected_maturing ? `Still maturing — ${d.collected_days_stale}d, more to post` : "Tied to visit dates"} icon={Wallet} tone={C.purple}
                  spark={{ data: d.collected_by_month, key: 'collected' }} />
        <StatTile testid="bi-kpi-lost-income" label="Lost Income YTD (est.)" value={CURRENCY_SHORT(k.lost_income_est)} sub={`${NUM(k.cancels + k.no_shows)} missed visits · est. from avg $/session`} icon={AlertTriangle} tone={C.rose} />
        <StatTile testid="bi-kpi-rev-per-session" label="Rev / Session" value={CURRENCY(k.rev_per_session)} sub="Per attended visit" icon={Activity} tone={C.teal} />
        <StatTile testid="bi-kpi-unique-clients" label="Active Clients" value={NUM(k.unique_clients)} sub={CURRENCY(k.rev_per_client)+ ' avg / client'} icon={Users} tone={C.purple} />
      </div>
      <KpiTrendRow d={d} />
      <div className="mb-8" />

      {/* MARGIN / SESSION — payroll-derived, defines company profitability */}
      {payrollTrend.current && (
        <>
          <SectionTitle title="Margin per session" subtitle="Latest finalized period vs avg of prior 3 · are we trending in the right direction?" right={
            <span className="text-[11px]" style={{ color: C.soft }} data-testid="bi-margin-data-as-of">
              Period ending {fmtDate(payrollTrend.current?.run?.period_end)} · Data as of last SimplePractice upload
            </span>
          } />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            <StatTile testid="bi-kpi-income-per-session"
                      label="Income / Session"
                      value={CURRENCY(_incPerSess)}
                      sub={_avgInc ? `3-mo avg ${_money(_avgInc)} — want ▲` : 'Not enough history yet'}
                      delta={_incDelta}
                      invertDelta={false}
                      icon={DollarSign}
                      tone={C.gold}
                      spark={{ data: _payrollSparkIncome, key: 'income_per_session' }} />
            <StatTile testid="bi-kpi-payroll-per-session"
                      label="Payroll / Session"
                      value={CURRENCY(_payPerSess)}
                      sub={_avgPay ? `3-mo avg ${_money(_avgPay)} — want ▼` : 'Not enough history yet'}
                      delta={_payDelta}
                      invertDelta={true}
                      icon={Wallet}
                      tone={C.rose}
                      spark={{ data: _payrollSparkIncome, key: 'payroll_per_session' }} />
            <StatTile testid="bi-kpi-margin-per-session"
                      label="Margin / Session"
                      value={CURRENCY(_marginPerSess)}
                      sub={_avgMar ? `3-mo avg ${_money(_avgMar)} — want ▲` : `${(_incPerSess ? (_marginPerSess / _incPerSess) * 100 : 0).toFixed(1)}% margin`}
                      delta={_marDelta}
                      invertDelta={false}
                      icon={TrendingUp}
                      tone={_marginPerSess >= 0 ? C.teal : C.rose}
                      spark={{ data: _payrollSparkIncome, key: 'margin_per_session' }} />
          </div>
        </>
      )}

      {/* MOMENTUM CHART — bar chart with month labels + on-bar $ labels (2026-02 fix) */}
      <SectionTitle title="Revenue momentum" subtitle="Monthly net income — the story of your top line" />
      <Card className="p-5 rounded-2xl mb-4 bg-white border" style={{ borderColor: C.border }}>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={d.income_by_month || []} margin={{ top: 30, right: 20, left: 8, bottom: 8 }} barCategoryGap="18%">
            <CartesianGrid strokeDasharray="3 3" stroke={C.hair} vertical={false} />
            <XAxis
              dataKey="month"
              stroke={C.soft}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              interval={0}
              data-testid="bi-momentum-xaxis"
            />
            <YAxis stroke={C.soft} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => CURRENCY(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(31,26,44,0.08)' }} />
            <Bar dataKey="net" name="Net" fill={C.purple} radius={[6, 6, 0, 0]}>
              <LabelList
                dataKey="net"
                position="top"
                formatter={(v) => (v == null ? '' : CURRENCY_SHORT(v))}
                style={{ fill: C.ink, fontSize: 11, fontWeight: 600 }}
              />
            </Bar>
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* MONTHLY SESSIONS — stacked bar: Shows + No Shows + Late Cancels per month. */}
      <SectionTitle title="Monthly Sessions" subtitle="Attended visits, no-shows, and late cancels per month · total labeled on top" />
      <Card className="p-5 rounded-2xl mb-8 bg-white border" style={{ borderColor: C.border }} data-testid="bi-monthly-sessions">
        <ResponsiveContainer width="100%" height={340}>
          <BarChart
            data={(d.show_by_month || []).map(m => ({
              ...m,
              total: (m.shows || 0) + (m.no_shows || 0) + (m.late_cancels || 0),
            }))}
            margin={{ top: 30, right: 20, left: 8, bottom: 8 }}
            barCategoryGap="18%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke={C.hair} vertical={false} />
            <XAxis
              dataKey="month"
              stroke={C.soft}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              interval={0}
              data-testid="bi-monthly-sessions-xaxis"
            />
            <YAxis stroke={C.soft} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => NUM(v)} />
            <Tooltip formatter={(v) => NUM(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(31,26,44,0.08)' }} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
            <Bar dataKey="shows" name="Shows" stackId="s" fill={C.teal} />
            <Bar dataKey="no_shows" name="No Shows" stackId="s" fill={C.rose} />
            <Bar dataKey="late_cancels" name="Late Cancels" stackId="s" fill={C.amber} radius={[6, 6, 0, 0]}>
              <LabelList
                dataKey="total"
                position="top"
                formatter={(v) => (v == null ? '' : NUM(v))}
                style={{ fill: C.ink, fontSize: 11, fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
}

// ─── Financial tab ───
function FinancialTab({ d }) {
  const k = d.kpis || {};
  const payerDist = d.payer_distribution || [];
  // Stable ref for downstream useMemo hooks — prevents new-array-per-render
  // from re-invalidating `payers` / `filteredDD` on every parent tick.
  const dd = useMemo(() => d.cpt_payer_deep_dive || [], [d.cpt_payer_deep_dive]);
  const othersBreakdown = d.cpt_others_breakdown || [];

  // iter 72 — Deep-dive controls: filter the CPT table by a specific payer
  // or show the "System-wide Average" row (payer='__all__') per CPT.
  const [payerFilter, setPayerFilter] = useState('__all__');
  const [showOthers, setShowOthers] = useState(false);
  const payers = useMemo(() => {
    const set = new Set(dd.map(r => r.payer).filter(p => p && p !== '__all__'));
    return Array.from(set).sort();
  }, [dd]);
  const filteredDD = useMemo(() => (dd || []).filter(r => r.payer === payerFilter), [dd, payerFilter]);
  // Total Collected is now anchored to the Income & Fees report (deposits) — the source of truth for cash received.
  // Appointment-Status posting can lag or misfire (rate mismatches, unposted payments), so we no longer sum the
  // payer_distribution payer totals for the headline KPI. `k.gross_income_ytd` = SUM(client_pay + ins_pay) from
  // the Income & Fees export for the months in scope. The payer_distribution sum is retained below only to
  // annotate the tile with the divergence when the two sources disagree.
  const totalCollectedIncome = Number(k.gross_income_ytd || 0);
  const payerDistSum = payerDist.reduce((s, p) => s + (p.collected || 0), 0);
  const _incomeSessionsBasis = Number(k.sessions_held || 0);
  const _incomePerSessionAvg = _incomeSessionsBasis ? totalCollectedIncome / _incomeSessionsBasis : 0;
  const _postingDrift = totalCollectedIncome - payerDistSum;
  const _postingDriftPct = totalCollectedIncome ? _postingDrift / totalCollectedIncome : 0;
  const _driftMaterial = Math.abs(_postingDriftPct) >= 0.02; // ≥2% mismatch surfaces a warning

  return (
    <>
      <SectionTitle
        title="Total Collected"
        subtitle="Anchored to the Income & Fees report (cash actually deposited). Appointment-Status posting can lag or misfire, so this KPI uses the deposit-basis figure instead of summing appointment-status payments."
      />
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <StatTile
          testid="bi-total-collected"
          label="Total Collected · Income report"
          value={CURRENCY(totalCollectedIncome)}
          sub={_driftMaterial
            ? `Appt-Status sum ${CURRENCY(payerDistSum)} (${_postingDrift >= 0 ? '−' : '+'}${Math.abs(_postingDriftPct*100).toFixed(1)}% posting drift)`
            : `Client + insurance payments received · Appt-Status sum ${CURRENCY(payerDistSum)} (in-line)`}
          icon={DollarSign}
          tone={C.gold}
        />
        <StatTile testid="bi-sessions"        label="Appointments Billed" value={NUM(k.sessions_held)}  sub="Distinct appointments from Appointment Status. CPT-line total can exceed this when a visit bills multiple codes." icon={Activity}   tone={C.teal} />
        <StatTile
          testid="bi-rev-per-session"
          label="Avg $ / Session"
          value={CURRENCY(_incomePerSessionAvg)}
          sub="Income & Fees deposits ÷ attended appointments · mixes deposit month with DOS visits, so treat as a directional indicator."
          icon={Wallet}
          tone={C.purple}
        />
      </div>
      {_driftMaterial && (
        <Card
          className="p-3 rounded-2xl mb-6 border flex items-start gap-2 text-[12px]"
          style={{ background: '#F7EFDA', borderColor: '#B08A3A55', color: '#7A5F16' }}
          data-testid="bi-total-collected-drift"
        >
          <AlertTriangle size={14} style={{ marginTop: 2 }} />
          <div>
            <span style={{ fontWeight: 700 }}>Posting drift detected.</span> Income & Fees deposits are {CURRENCY(totalCollectedIncome)} but summing payer-level payments from Appointment Status yields {CURRENCY(payerDistSum)}
            &nbsp;({_postingDriftPct >= 0 ? '+' : '−'}{Math.abs(_postingDriftPct*100).toFixed(1)}%). The Total Collected tile uses the Income report because deposits are the source of truth; the payer & CPT tables below still reflect Appointment-Status attributions so shortfalls are visible where they occur.
          </div>
        </Card>
      )}

      {/* iter 79 — Monthly combo chart: bars = avg $/session, line =
          number of appointments that month. Two Y-axes because the units
          are different (dollars vs count). */}
      <SectionTitle title="Monthly Revenue Health" subtitle="Bars: avg $/session · Line: appointments billed that month" />
      <Card className="p-5 rounded-2xl mb-8 bg-white border" style={{ borderColor: C.border }} data-testid="bi-monthly-combo-chart">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={d.revenue_by_month || []}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.hair} vertical={false} />
            <XAxis dataKey="month" stroke={C.soft} fontSize={11} tickLine={false} tickFormatter={fmtMonYY} />
            <YAxis yAxisId="left"  stroke={C.purple} fontSize={11} tickLine={false}
                   tickFormatter={(v) => `$${Math.round(v)}`}
                   label={{ value: 'Avg $ / session', angle: -90, position: 'insideLeft', style: { fill: C.purple, fontSize: 11 } }} />
            <YAxis yAxisId="right" stroke={C.teal}   fontSize={11} tickLine={false}
                   orientation="right"
                   tickFormatter={(v) => NUM(v)}
                   label={{ value: 'Appointments', angle: 90, position: 'insideRight', style: { fill: C.teal, fontSize: 11 } }} />
            <Tooltip
              itemSorter={(a) => -Number(a.value || 0)}
              formatter={(v, n) => n === 'Appointments' ? NUM(v) : CURRENCY(v)}
              contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar  yAxisId="left"  dataKey="avg_per_session" name="Avg $ / session" fill={C.purple} radius={[6,6,0,0]} />
            <Line yAxisId="right" dataKey="sessions"        name="Appointments"    stroke={C.teal} strokeWidth={2.5} dot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* iter 77 — Monthly avg-paid trend for the four main CPT codes.
          Uses the pre-computed cpt_by_month array where each entry is
          {month, '92507', '92609', '92523', '92526'} = avg $/session that
          month. Every other CPT rolls up into the "Others" bucket in the
          deep-dive table below (kept off this chart to keep it readable). */}
      <SectionTitle title="Avg $ / Session — monthly trend, main CPT codes" subtitle="Collected (client_paid + ins_paid) ÷ Show appointments, by CPT · 92507 · 92609 · 92526 · faded bars = still being paid (insurance posts up to ~70 days after the visit)" />
      <CptTrendChart rows={d.cpt_by_month || []} lagDays={d.cpt_maturity_trend?.lag_days || 70} />

      <SectionTitle
        title="Avg Paid per CPT Code"
        subtitle="Filter by payer or view the system-wide average · use avg/session to forecast by appointment count, avg/unit for line-item projections"
        right={
          <select
            value={payerFilter}
            onChange={(e) => setPayerFilter(e.target.value)}
            data-testid="bi-cpt-payer-filter"
            className="text-sm rounded-full border px-3 py-1.5"
            style={{ borderColor: C.border, background: 'white', color: C.ink2 }}>
            <option value="__all__">System-wide Average</option>
            {payers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        }
      />
      {(() => {
        const mt = d.cpt_maturity_trend;
        if (!mt || !mt.rows || !mt.rows.length) return null;
        const CPT_LABEL = { '92507': 'Treatment', '92523': 'Eval', '92526': 'Feeding', '92609': 'AAC' };
        const fmtYM = (ym) => {
          if (!ym) return '—';
          const [y, m] = ym.split('-'); const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          return `${names[parseInt(m,10)-1]} ${y}`;
        };
        const windowLabel = mt.prior_window_months && mt.prior_window_months.length
          ? `${fmtYM(mt.prior_window_months[0])} – ${fmtYM(mt.prior_window_months[mt.prior_window_months.length-1])}`
          : '—';
        return (
          <Card className="p-5 rounded-2xl mb-4 bg-white border" style={{ borderColor: C.border }} data-testid="bi-cpt-maturity-trend">
            <div className="flex items-start justify-between mb-3 gap-6 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Reimbursement Trend · {mt.lag_days}-day lag adjusted</div>
                <h3 className="font-serif text-lg mt-1" style={{ color: C.ink }}>
                  {fmtYM(mt.latest_mature_month)} <span className="text-[12px] font-sans font-normal" style={{ color: C.soft }}>vs</span> {windowLabel} <span className="text-[12px] font-sans font-normal" style={{ color: C.soft }}>(prior 3-mo avg)</span>
                </h3>
                <div className="text-[11px] mt-1" style={{ color: C.soft }}>
                  Only Show appointments; latest mature month = calendar month ending ≥ {mt.lag_days} days ago so insurance has had time to post. Up arrow = reimbursement rate climbing vs the prior 3 months.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bi-cpt-maturity-cards">
              {mt.rows.map(r => {
                const up = r.delta_pct > 0.1;
                const down = r.delta_pct < -0.1;
                const flat = !up && !down;
                const color = up ? '#3F7D6E' : down ? '#8A3A3A' : C.soft;
                const bg    = up ? '#EEF4F2' : down ? '#FBEDED' : '#F6F5F1';
                const arrow = up ? '▲' : down ? '▼' : '●';
                const sign  = r.delta_pct > 0 ? '+' : '';
                return (
                  <div key={r.code} className="rounded-xl p-3 border" style={{ borderColor: C.hair, background: bg }} data-testid={`bi-cpt-maturity-${r.code}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-semibold" style={{ color: C.ink }}>{r.code}</div>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>{CPT_LABEL[r.code] || ''}</div>
                    </div>
                    <div className="mt-2 text-2xl font-serif tabular-nums" style={{ color: C.ink }}>
                      {CURRENCY(r.latest_avg)}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: C.soft }}>
                      {r.latest_n ? `${NUM(r.latest_n)} Shows` : 'no Shows'} · prior {CURRENCY(r.prior_avg)}
                      {r.small_sample && <span className="ml-1 font-semibold" style={{ color: '#8A3A3A' }} data-testid={`bi-cpt-maturity-small-${r.code}`}>· small sample</span>}
                    </div>
                    <div className="mt-1.5 text-[13px] font-semibold tabular-nums flex items-center gap-1" style={{ color }}>
                      <span style={{ fontSize: 11 }}>{arrow}</span>
                      <span>{sign}{r.delta_pct.toFixed(1)}%</span>
                      <span className="text-[10px] font-normal" style={{ color: C.soft }}> vs 3-mo avg</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}
      <Card className="p-5 rounded-2xl mb-4 bg-white border overflow-hidden" style={{ borderColor: C.border }}>
        <table className="w-full text-sm" data-testid="bi-cpt-payer-table">
          <thead>
            <tr className="text-left uppercase tracking-widest text-[10px]" style={{ color: C.soft, borderBottom: `1px solid ${C.border}` }}>
              <th className="py-2">CPT Code</th>
              <th className="py-2 text-right">Sessions</th>
              <th className="py-2 text-right">Units</th>
              <th className="py-2 text-right">Total Collected</th>
              <th className="py-2 text-right">Avg $ / Session</th>
              <th className="py-2 text-right">Avg $ / Unit</th>
            </tr>
          </thead>
          <tbody>
            {filteredDD.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center" style={{ color: C.soft }}>No sessions for this payer yet.</td></tr>
            ) : filteredDD.map(r => (
              <React.Fragment key={r.code + r.payer}>
                <tr
                  className={`border-b hover:bg-[#F7EFDA] ${r.code === 'Others' && othersBreakdown.length > 0 ? 'cursor-pointer' : ''}`}
                  style={{ borderColor: C.hair }}
                  data-testid={`cpt-row-${r.code}`}
                  onClick={() => { if (r.code === 'Others' && othersBreakdown.length > 0) setShowOthers(v => !v); }}
                >
                  <td className="py-2 font-semibold flex items-center gap-1.5" style={{ color: C.ink }}>
                    {r.code === 'Others' && othersBreakdown.length > 0 && (
                      <ChevronRight
                        size={14}
                        className="transition-transform"
                        style={{ transform: showOthers ? 'rotate(90deg)' : 'none', color: C.soft }}
                        data-testid="cpt-others-toggle"
                      />
                    )}
                    {r.code}
                  </td>
                  <td className="py-2 text-right">{NUM(r.sessions)}</td>
                  <td className="py-2 text-right">{NUM(r.units)}</td>
                  <td className="py-2 text-right font-medium">{CURRENCY(r.collected)}</td>
                  <td className="py-2 text-right">{CURRENCY(r.avg_per_session)}</td>
                  <td className="py-2 text-right">{CURRENCY(r.avg_per_unit)}</td>
                </tr>
                {r.code === 'Others' && showOthers && othersBreakdown.map(o => (
                  <tr key={`o-${o.code}`} className="border-b" style={{ borderColor: C.hair, background: '#FBF9F5' }} data-testid={`cpt-others-child-${o.code}`}>
                    <td className="py-1.5 pl-8 text-xs" style={{ color: C.soft }}>
                      <span className="font-mono">{o.code}</span>
                    </td>
                    <td className="py-1.5 text-right text-xs" style={{ color: C.soft }}>{NUM(o.sessions)}</td>
                    <td className="py-1.5 text-right text-xs" style={{ color: C.soft }}>{NUM(o.units)}</td>
                    <td className="py-1.5 text-right text-xs" style={{ color: C.soft }}>{CURRENCY(o.collected)}</td>
                    <td className="py-1.5 text-right text-xs" style={{ color: C.soft }}>{CURRENCY(o.avg_per_session)}</td>
                    <td className="py-1.5 text-right text-xs" style={{ color: C.soft }}>{CURRENCY(o.avg_per_unit)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ─── Operations tab ───
function OperationsTab({ d }) {
  const k = d.kpis || {};
  const [e2t, setE2t] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    api.get('/dashboard/monthly-tables', { params: { months: 12, scope: 'group' } })
      .then(r => setE2t({
        all: r.data?.eval_to_therapy || [],
        by_clinic: r.data?.eval_to_therapy_by_clinic || {},
      }))
      .catch(() => setE2t({ all: [], by_clinic: {} }));
    api.get('/bi/intake-funnel-trend')
      .then(r => setFunnel(r.data))
      .catch(() => setFunnel({ months: [], latest_3mo: {}, prior_3mo: {}, delta: {} }));
  }, []);
  return (
    <>
      <SectionTitle title="Intake funnel speed" subtitle="How many calendar days from referral → eval → therapy start · 3-month trailing avg · negative delta = getting faster" />
      <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="bi-intake-funnel">
        {(() => {
          if (!funnel) return <div className="text-[11px]" style={{ color: C.soft }}>Loading…</div>;
          const months = funnel.months || [];
          const latest = funnel.latest_3mo || {};
          const prior  = funnel.prior_3mo || {};
          const delta  = funnel.delta || {};
          const Tile = ({ label, latestVal, priorVal, deltaVal, suffix='d' }) => {
            const improving = deltaVal != null && deltaVal < 0;   // fewer days = better
            const worsening = deltaVal != null && deltaVal > 0;
            const dColor = improving ? '#3F7D6E' : worsening ? '#8A3A3A' : C.soft;
            const dBg    = improving ? '#EEF4F2' : worsening ? '#FBEDED' : '#F6F5F1';
            const arrow  = improving ? '▼' : worsening ? '▲' : '●';
            return (
              <div className="rounded-xl p-4 border" style={{ borderColor: C.hair, background: dBg }} data-testid={`bi-funnel-${label.replace(/\W+/g,'_')}`}>
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>{label}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-serif text-3xl tabular-nums" style={{ color: C.ink }}>{latestVal != null ? latestVal.toFixed(1) : '—'}</div>
                  <div className="text-[12px]" style={{ color: C.soft }}>{suffix} · last 3 mo</div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-[11.5px]" style={{ color: dColor }}>
                  <span style={{ fontSize: 10 }}>{arrow}</span>
                  <span className="font-semibold tabular-nums">
                    {deltaVal == null ? '—' : `${deltaVal >= 0 ? '+' : ''}${deltaVal.toFixed(1)}${suffix}`}
                  </span>
                  <span style={{ color: C.soft }}>vs prior 3 mo ({priorVal != null ? priorVal.toFixed(1) : '—'}{suffix})</span>
                </div>
              </div>
            );
          };
          return (
            <>
              <div className="grid md:grid-cols-2 gap-3 mb-5">
                <Tile label="Referral → Eval" latestVal={latest.r2e} priorVal={prior.r2e} deltaVal={delta.r2e_days} />
                <Tile label="Eval → Therapy start" latestVal={latest.e2t} priorVal={prior.e2t} deltaVal={delta.e2t_days} />
              </div>
              {months.length > 0 && (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={months}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.hair} />
                    <XAxis dataKey="label" stroke={C.soft} fontSize={11} />
                    <YAxis stroke={C.soft} fontSize={11} unit="d" />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }}
                      formatter={(v, n) => [v != null ? `${Number(v).toFixed(1)} d` : '—', n]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="r2e_avg" fill="#B08A3A" name="Referral→Eval (monthly)" radius={[4,4,0,0]} />
                    <Line type="monotone" dataKey="r2e_3mo" stroke="#8A6A1F" strokeWidth={2.5} name="Referral→Eval (3-mo avg)" dot={false} />
                    <Bar dataKey="e2t_avg" fill="#5B4A7A" name="Eval→Therapy (monthly)" radius={[4,4,0,0]} />
                    <Line type="monotone" dataKey="e2t_3mo" stroke="#3F7D6E" strokeWidth={2.5} name="Eval→Therapy (3-mo avg)" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              <div className="mt-2 text-[10.5px]" style={{ color: C.soft }}>
                Bars = that month's single-month average. Line = trailing 3-month weighted avg (fewer wiggles). Only clients whose referral landed in the last 24 months count. Transitions &gt; 365 days are treated as data-entry outliers and excluded.
              </div>
            </>
          );
        })()}
      </Card>
      <SectionTitle title="Operational vitals" subtitle="The engine room — visits, shows, cancels" />
      {/* Attendance reconciliation — the executive-facing proof that the four buckets
          sum to Scheduled. Client-side reconciliation from `kpis` fields already
          present on the snapshot. Clinician cancellations stay in their own category
          (they are NOT rolled into client attendance figures). */}
      <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="bi-attendance-reconciliation">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-bold mb-3" style={{ color: C.gold }}>
          <ShieldAlert size={12} /> Attendance reconciliation · YTD
        </div>
        {(() => {
          const shows = Number(k.sessions_held || 0);
          const clientCxl = Number(k.cancels || 0);
          const noShows = Number(k.no_shows || 0);
          const clinCxl = Number(k.clinician_cancels || 0);
          const sched = Number(k.scheduled || 0);
          const sumParts = shows + clientCxl + noShows + clinCxl;
          const reconciles = sched > 0 && sumParts === sched;
          const showRateReported = k.show_rate;
          const showRateComputed = sched ? shows / sched : 0;
          const rateMatches = Math.abs((showRateReported || 0) - showRateComputed) < 0.0005;
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[12px]" style={{ color: C.ink2 }}>
                <div className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Shows</div>
                  <div className="font-serif text-lg mt-1" style={{ color: C.teal }}>{NUM(shows)}</div>
                </div>
                <div className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Client cancels</div>
                  <div className="font-serif text-lg mt-1" style={{ color: C.amber }}>{NUM(clientCxl)}</div>
                </div>
                <div className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>No-shows</div>
                  <div className="font-serif text-lg mt-1" style={{ color: C.rose }}>{NUM(noShows)}</div>
                </div>
                <div className="rounded-xl border p-3" style={{ borderColor: C.border }}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Clinician cancels · own category</div>
                  <div className="font-serif text-lg mt-1" style={{ color: C.rose }}>{NUM(clinCxl)}</div>
                </div>
                <div className="rounded-xl border-2 p-3" style={{ borderColor: reconciles ? C.teal : C.rose }}>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: reconciles ? C.teal : C.rose }}>Scheduled (should equal sum)</div>
                  <div className="font-serif text-lg mt-1" style={{ color: C.ink }}>{NUM(sched)}</div>
                </div>
              </div>
              <div className="mt-3 text-[12px] flex flex-wrap gap-4" style={{ color: reconciles ? C.teal : C.rose }}>
                <span data-testid="bi-recon-sum">Shows + Client cxl + No-shows + Clinician cxl = <span className="font-bold">{NUM(sumParts)}</span> {reconciles ? '= Scheduled ✓' : `≠ Scheduled ${NUM(sched)} — investigate`}</span>
                <span data-testid="bi-recon-show-rate" style={{ color: rateMatches ? C.teal : C.rose }}>Show rate {(showRateComputed*100).toFixed(1)}% = Shows ÷ Scheduled {rateMatches ? '✓ matches KPI' : `(KPI reports ${((showRateReported||0)*100).toFixed(1)}%)`}</span>
              </div>
              <div className="mt-2 text-[11px]" style={{ color: C.soft }}>
                Clinician cancellations are kept in their own category and NOT rolled into client attendance measures.
              </div>
            </>
          );
        })()}
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
        <StatTile testid="bi-kpi-sessions-held" label="Sessions Held" value={NUM(k.sessions_held)} sub="Shows YTD" icon={CheckCircle2} tone={C.teal} />
        <StatTile testid="bi-kpi-scheduled" label="Scheduled" value={NUM(k.scheduled)} sub="All appointments" icon={Activity} tone={C.purple} />
        <StatTile testid="bi-kpi-show-rate" label="Show Rate" value={PCT(k.show_rate)} sub="Shows ÷ Scheduled" icon={Target} tone={C.teal} delta={k.show_rate_delta} />
        <StatTile testid="bi-kpi-cancels" label="Cancels" value={NUM(k.cancels)} sub="Client-initiated" icon={AlertTriangle} tone={C.amber} />
        <StatTile testid="bi-kpi-no-shows" label="No-Shows" value={NUM(k.no_shows)} sub="Missed w/o notice" icon={AlertTriangle} tone={C.rose} />
        <StatTile testid="bi-kpi-clinician-cancels" label="Clinician Cancels" value={NUM(k.clinician_cancels)} sub="Staff-initiated" icon={AlertTriangle} tone={C.rose} />
        <StatTile testid="bi-kpi-rev-per-client" label="Rev / Client" value={CURRENCY_SHORT(k.rev_per_client)} sub="YTD per unique client" icon={Users} tone={C.gold} />
      </div>

      {/* Eval → Session Conversion (iter 90 — clinician request). Bar-and-
          line composite with a per-clinic filter, sitting near the top of
          Ops so ODs see it before diving into show-rate detail. */}
      <SectionTitle title="Eval → Session conversion" subtitle="Of the evals completed each month, how many turned into ongoing therapy" />
      <div className="mb-6" data-testid="bi-eval-to-session">
        {e2t
          ? <EvalToSessionChart data={e2t.all} byClinic={e2t.by_clinic} />
          : <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }}>
              <div className="text-[12px]" style={{ color: C.soft }}>Loading eval → session trend…</div>
            </Card>
        }
      </div>

      <SectionTitle title="Show rate — the trend" subtitle="Below 80% is a red line. Below 82% is a warning." />
      <Card className="p-5 rounded-2xl mb-4 bg-white border" style={{ borderColor: C.border }}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={(() => {
            const arr = d.show_by_month || [];
            // Simple 3-point centered rolling mean for the trend overlay.
            return arr.map((row, i) => {
              const win = arr.slice(Math.max(0, i - 1), Math.min(arr.length, i + 2));
              const mean = win.length ? win.reduce((s, x) => s + (x.show_rate || 0), 0) / win.length : row.show_rate;
              return { ...row, trend: mean };
            });
          })()}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.hair} vertical={false} />
            <XAxis dataKey="month" stroke={C.soft} fontSize={11} tickLine={false} tickFormatter={fmtMonYY} />
            <YAxis stroke={C.soft} fontSize={11} tickLine={false} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v*100)}%`} />
            <Tooltip formatter={(v, n) => n === 'trend' ? PCT(v) : n === 'show_rate' ? PCT(v) : NUM(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <ReferenceLine y={0.82} stroke={C.amber} strokeDasharray="4 4" label={{ value: 'Warning 82%', position: 'insideRight', fontSize: 10, fill: C.amber }} />
            <ReferenceLine y={0.80} stroke={C.rose}  strokeDasharray="4 4" label={{ value: 'Red line 80%', position: 'insideRight', fontSize: 10, fill: C.rose }} />
            <Bar dataKey="show_rate" name="Show rate" fill={C.purple} radius={[6,6,0,0]} maxBarSize={38} />
            <Line dataKey="trend" name="Trend (3-mo mean)" stroke={C.gold} strokeWidth={2.5} dot={false} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      <SectionTitle title="Estimated lost income — monthly" subtitle="What you'd have earned if every cancel & no-show had happened" />
      <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={d.lost_by_month || []}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.hair} vertical={false} />
            <XAxis dataKey="month" stroke={C.soft} fontSize={11} tickLine={false} tickFormatter={fmtMonYY} />
            <YAxis stroke={C.soft} fontSize={11} tickLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => CURRENCY(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
            <Bar dataKey="lost" fill={C.rose} radius={[6,6,0,0]} name="Est. lost $" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <SectionTitle title="Top-15 outreach targets" subtitle="Clients with the most missed appointments — potential recovery revenue" />
      <Card className="rounded-2xl overflow-hidden bg-white border" style={{ borderColor: C.border }}>
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full text-[13px]" data-testid="bi-outreach-table">
            <thead className="text-[10px] uppercase tracking-wider sticky top-0 z-10" style={{ background: C.paper, color: C.soft }}>
              <tr>
                <th className="text-left py-3 px-4">Client</th>
                <th className="text-left py-3 px-4">Clinic</th>
                <th className="text-right py-3 px-4">Shows</th>
                <th className="text-right py-3 px-4">Cancels</th>
                <th className="text-right py-3 px-4">No-Shows</th>
                <th className="text-right py-3 px-4">Missed</th>
                <th className="text-right py-3 px-4">Est. Recovery</th>
              </tr>
            </thead>
            <tbody>
              {(d.outreach || []).map((o, i) => (
                <tr key={i} className="border-t" style={{ borderColor: C.hair }}>
                  <td className="py-3 px-4 font-medium" style={{ color: C.ink }}>{o.client}</td>
                  <td className="py-3 px-4" style={{ color: C.ink2 }}>{o.clinic}</td>
                  <td className="py-3 px-4 text-right">{o.shows}</td>
                  <td className="py-3 px-4 text-right">{o.cxl}</td>
                  <td className="py-3 px-4 text-right">{o.ns}</td>
                  <td className="py-3 px-4 text-right font-semibold" style={{ color: C.rose }}>{o.missed}</td>
                  <td className="py-3 px-4 text-right font-serif" style={{ color: C.gold }}>{CURRENCY(o.est_recovery)}</td>
                </tr>
              ))}
              {!d.outreach?.length && <tr><td colSpan={7} className="text-center py-8" style={{ color: C.soft }}>No missed-appointment data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ─── Payer Family Rollup — BI-only hierarchy (does NOT modify shared payer normalization)
// Groups the snapshot's already-computed `payer_intel[]` rows into consistent family
// buckets so payer-level views reconcile with the CPT rollup in Financial. This is a
// read-only client-side transform — no DB writes, no changes to bi/snapshot.py or the
// payer_alloc / payer_aliases collections. If a payer belongs to a family, its own
// row still appears nested inside the family card so nothing is silently deduplicated.
function _classifyPayerFamily(name) {
  const s = String(name || '').toLowerCase();
  if (!s || s === 'other' || s === 'others') return 'Others';
  if (s.includes('self-pay') || s.includes('self pay') || s.includes('unknown')) return 'Self-Pay / Unknown';
  if (s.includes('united') || s.startsWith('umr') || s.includes('surest') || s.includes('uhc')) return 'United Healthcare Family';
  if (s.includes('blue cross') || s.includes('bcbs') || s.includes('anthem bcbs') || s.includes('blueshield') || s.includes('blue shield')) return 'BCBS Family';
  if (s.includes('cigna')) return 'Cigna Family';
  if (s.includes('aetna')) return 'Aetna Family';
  if (s.includes('humana')) return 'Humana Family';
  if (s.includes('tricare') || s.includes('champva') || s.includes('champus')) return 'TRICARE / VA Family';
  if (s.includes('medicare')) return 'Medicare Family';
  if (s.includes('medicaid') || s.includes('star kids') || s.includes('star+plus')
      || s.includes('amerigroup') || s.includes('molina') || s.includes('community health choice')
      || s.includes('superior') || s.includes('parkland') || s.includes('driscoll')
      || s.includes('texas children') || s.includes('chip')) return 'Medicaid / CHIP Family';
  return 'Other Commercial';
}

function PayerFamilyRollup({ rows }) {
  const families = useMemo(() => {
    const acc = {};
    (rows || []).forEach(r => {
      const fam = _classifyPayerFamily(r.payer);
      const b = acc[fam] || (acc[fam] = {
        family: fam, collected: 0, expected: 0, sessions: 0,
        claim_total: 0, claim_denied: 0, gap: 0, members: [],
      });
      b.collected   += Number(r.collected || 0);
      b.expected    += Number(r.expected  || 0);
      b.sessions    += Number(r.sessions  || 0);
      b.claim_total += Number(r.claim_total || 0);
      b.claim_denied+= Number(r.claim_denied || 0);
      b.gap         += Math.max(0, Number(r.gap || 0));
      b.members.push(r);
    });
    Object.values(acc).forEach(b => {
      // Ratios recomputed from summed numerators/denominators — never averaged across payers.
      b.realization = b.expected ? b.collected / b.expected : 0;
      b.denial_rate = b.claim_total ? b.claim_denied / b.claim_total : 0;
      b.members.sort((x, y) => (y.collected || 0) - (x.collected || 0));
    });
    return Object.values(acc).sort((a, b) => b.collected - a.collected);
  }, [rows]);

  const totalCollected = families.reduce((s, f) => s + f.collected, 0) || 1;
  const rowsTotalCollected = (rows || []).reduce((s, r) => s + Number(r.collected || 0), 0);
  const reconciles = Math.abs(totalCollected - rowsTotalCollected) < 1;

  const [openFam, setOpenFam] = useState({});

  if (!families.length) return null;

  return (
    <>
      <SectionTitle
        title="Payer family reconciliation · BI-only hierarchy"
        subtitle="Rows below are the same payers you see everywhere else, grouped into consistent parent families so payer, CPT × payer, and Financial totals reconcile under one grouping."
        right={
          <span
            className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
            style={{
              background: reconciles ? '#EAF2EE' : '#F5EAEA',
              color: reconciles ? C.teal : C.rose,
              borderColor: reconciles ? '#839E9744' : '#8A3A3A55',
            }}
            data-testid="bi-payer-family-reconciliation"
          >
            Family total {CURRENCY(totalCollected)} {reconciles ? '= Payer sum ✓' : `≠ Payer sum ${CURRENCY(rowsTotalCollected)} — investigate`}
          </span>
        }
      />
      <Card className="p-0 rounded-2xl mb-6 bg-white border overflow-hidden" style={{ borderColor: C.border }} data-testid="bi-payer-family-table">
        <table className="w-full text-[13px]">
          <thead className="text-[10px] uppercase tracking-wider" style={{ background: C.paper, color: C.soft }}>
            <tr>
              <th className="text-left  py-3 px-4">Family / member</th>
              <th className="text-right py-3 px-3">Sessions</th>
              <th className="text-right py-3 px-3">Collected</th>
              <th className="text-right py-3 px-3">Expected</th>
              <th className="text-right py-3 px-3">Realization</th>
              <th className="text-right py-3 px-3">Denial rate</th>
              <th className="text-right py-3 px-3">Contract gap</th>
              <th className="text-right py-3 px-3">Share</th>
            </tr>
          </thead>
          <tbody>
            {families.map((f) => {
              const isOpen = !!openFam[f.family];
              const share = f.collected / totalCollected;
              const membersSum = f.members.reduce((s, m) => s + Number(m.collected || 0), 0);
              const memberRec = Math.abs(membersSum - f.collected) < 1;
              const realCol = f.realization >= 1 ? C.teal : f.realization >= 0.9 ? C.amber : C.rose;
              const denCol  = f.denial_rate < 0.03 ? C.teal : f.denial_rate < 0.07 ? C.amber : C.rose;
              return (
                <React.Fragment key={f.family}>
                  <tr
                    className="border-t cursor-pointer hover:bg-[#F7EFDA] transition-colors"
                    style={{ borderColor: C.hair, background: '#FBF8F4' }}
                    onClick={() => setOpenFam(o => ({ ...o, [f.family]: !o[f.family] }))}
                    data-testid={`bi-payer-family-row-${f.family.replace(/\W+/g, '-').toLowerCase()}`}
                  >
                    <td className="py-3 px-4 font-serif flex items-center gap-1.5" style={{ color: C.ink }}>
                      <ChevronRight size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', color: C.soft }} />
                      {f.family}
                      <span className="text-[10px] uppercase tracking-widest ml-2" style={{ color: C.soft }}>
                        {f.members.length} {f.members.length === 1 ? 'member' : 'members'}
                      </span>
                      {!memberRec && (
                        <span
                          className="text-[10px] font-semibold ml-2 px-1.5 py-0.5 rounded-full border"
                          style={{ background: '#F5EAEA', color: C.rose, borderColor: '#8A3A3A55' }}
                          title="Members do not sum to the family total"
                        >
                          reconcile?
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right">{NUM(f.sessions)}</td>
                    <td className="py-3 px-3 text-right font-serif" style={{ color: C.purple }}>{CURRENCY(f.collected)}</td>
                    <td className="py-3 px-3 text-right" style={{ color: C.ink2 }}>{CURRENCY(f.expected)}</td>
                    <td className="py-3 px-3 text-right font-semibold" style={{ color: realCol }}>{PCT(f.realization)}</td>
                    <td className="py-3 px-3 text-right font-semibold" style={{ color: denCol }}>{PCT(f.denial_rate)}</td>
                    <td className="py-3 px-3 text-right font-semibold" style={{ color: f.gap > 0 ? C.rose : C.soft }}>{CURRENCY(f.gap)}</td>
                    <td className="py-3 px-3 text-right tabular-nums" style={{ color: C.ink2 }}>{PCT(share)}</td>
                  </tr>
                  {isOpen && f.members.map((m, i) => (
                    <tr
                      key={`${f.family}-${m.payer}-${i}`}
                      className="border-t"
                      style={{ borderColor: C.hair, background: '#FFFFFF' }}
                      data-testid={`bi-payer-family-member-${f.family.replace(/\W+/g, '-').toLowerCase()}-${(m.payer || '').replace(/\W+/g, '-').toLowerCase()}`}
                    >
                      <td className="py-2 px-4 pl-10 text-[12px]" style={{ color: C.ink2 }}>{m.payer}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: C.soft }}>{NUM(m.sessions)}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: C.ink2 }}>{CURRENCY(m.collected)}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: C.soft }}>{CURRENCY(m.expected)}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: C.soft }}>{PCT(m.realization)}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: C.soft }}>{PCT(m.denial_rate)}</td>
                      <td className="py-2 px-3 text-right text-[12px]" style={{ color: m.gap > 0 ? C.rose : C.soft }}>{CURRENCY(m.gap || 0)}</td>
                      <td className="py-2 px-3 text-right text-[11px]" style={{ color: C.soft }}>{PCT((Number(m.collected) || 0) / totalCollected)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr className="border-t-2" style={{ borderColor: C.gold, background: '#FBF8F4' }} data-testid="bi-payer-family-total-row">
              <td className="py-3 px-4 font-serif" style={{ color: C.ink }}>Grand total — all families</td>
              <td className="py-3 px-3 text-right font-semibold">{NUM(families.reduce((s, f) => s + f.sessions, 0))}</td>
              <td className="py-3 px-3 text-right font-serif" style={{ color: C.gold }}>{CURRENCY(totalCollected)}</td>
              <td className="py-3 px-3 text-right" style={{ color: C.ink2 }}>{CURRENCY(families.reduce((s, f) => s + f.expected, 0))}</td>
              <td className="py-3 px-3 text-right"></td>
              <td className="py-3 px-3 text-right"></td>
              <td className="py-3 px-3 text-right font-semibold" style={{ color: C.rose }}>{CURRENCY(families.reduce((s, f) => s + f.gap, 0))}</td>
              <td className="py-3 px-3 text-right" style={{ color: C.ink2 }}>100.0%</td>
            </tr>
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ─── Clinical Quality & Experience tab — honest unavailable state (no synthetic score) ───
function ClinicalQualityTab({ d }) {
  const throughDate = d?.through_date ? fmtDate(d.through_date) : '—';
  return (
    <>
      <SectionTitle
        title="Clinical Quality & Experience"
        subtitle={`Section reserved for outcome measures, assessment completeness, goals / discharge reasons, continuity, and family feedback. Data through ${throughDate}.`}
      />
      <Card className="p-6 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="bi-quality-unavailable">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} style={{ color: C.amber, marginTop: 2 }} />
          <div className="min-w-0">
            <div className="font-serif text-lg" style={{ color: C.ink }}>No synthetic quality score is displayed</div>
            <div className="text-[13px] mt-1" style={{ color: C.ink2 }}>
              The underlying data feeds needed for a clinically valid quality view are not currently ingested to Executive BI.
              To avoid a false signal, this tab lists what would be shown here once those feeds are wired — and preserves the honest gap.
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-4 text-[12px]">
              {[
                { name: 'Outcome measure completion (per assessment window)', state: 'Unavailable',
                  why: 'Standardized outcome measure export (e.g. GFTA-3, PLS-5, CELF-5, per-goal progress) is not ingested.' },
                { name: 'Assessment completeness at intake', state: 'Unavailable',
                  why: 'Requires structured intake fields that are not yet exposed to the BI cube.' },
                { name: 'Goals status distribution & discharge reasons', state: 'Partial',
                  why: 'Discharge reasons captured in client records but not aggregated for BI; deep-link out to Client detail is preserved.' },
                { name: 'Continuity — same-clinician % across a plan of care', state: 'Partial',
                  why: 'Derivable from attendance + roster; not surfaced here. Available in Clinician Performance report.' },
                { name: 'Family feedback (CAHPS-like surveys, NPS)', state: 'Unavailable',
                  why: 'No survey feed ingested.' },
                { name: 'Missingness preserved', state: 'Rule',
                  why: 'Where a cohort has no eligible denominator, this tab surfaces a gap — no zero substitutions, no imputed averages, no synthetic composite score.' },
              ].map(row => (
                <div key={row.name} className="rounded-xl border p-3" style={{ borderColor: C.border, background: '#FBF8F4' }} data-testid={`bi-quality-row-${row.name.replace(/\W+/g,'-').toLowerCase().slice(0,40)}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div style={{ color: C.ink, fontWeight: 600 }}>{row.name}</div>
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{
                        background: row.state === 'Unavailable' ? '#F5EAEA' : row.state === 'Partial' ? '#F7EFDA' : '#EAF2EE',
                        color: row.state === 'Unavailable' ? C.rose : row.state === 'Partial' ? C.amber : C.teal,
                      }}
                    >
                      {row.state}
                    </span>
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: C.soft }}>{row.why}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[11px]" style={{ color: C.soft }}>
              Once outcome feeds are ingested, this tab will render clinically valid cohort views (not a single composite score) with n/N visible on every metric.
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

// ─── Payers tab ───
function PayersTab({ d }) {
  const k = d.kpis || {};
  const rows = d.payer_intel || [];
  const up = d.underpayment || {};
  const payerDist = d.payer_distribution || [];
  return (
    <>
      {/* ── 1. At-a-glance KPIs ─────────────────────────────────── */}
      <div className="grid lg:grid-cols-4 gap-3" data-testid="bi-payers-kpis">
        <StatTile label="Contract Gap Total" value={CURRENCY_SHORT(k.contract_gap_total)} sub="Money left on the table" icon={Target} tone={C.lavender} testid="bi-payers-gap" />
        <StatTile label="Underpaid (open)" value={CURRENCY_SHORT(k.underpaid_amount)} sub={`${NUM(k.underpaid_count)} claims in recovery queue`} icon={AlertTriangle} tone={C.rose} testid="bi-payers-underpaid" />
        <StatTile label="Denial Rate" value={PCT(k.denial_rate)} sub={`${NUM(k.denied_claims)} of ${NUM(k.total_claims)} claims`} icon={ShieldAlert} tone={C.rose} testid="bi-payers-denial" />
        <StatTile label="Top-3 Concentration" value={PCT(k.top3_payer_share)} sub="% of collected from top 3 payers" icon={PieIcon} tone={C.amber} testid="bi-payers-conc" />
      </div>

      {/* ── 2. Payer Distribution — who pays us ─────────────────── */}
      <section className="mt-10" data-testid="bi-payers-section-distribution">
        <SectionTitle title="Payer Distribution" subtitle="Share of Total Collected by Primary Insurance — sorted by dollars, hover a bar for details" />
        <div className="grid lg:grid-cols-5 gap-4 mt-4">
          <Card className="lg:col-span-3 p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-payer-distribution-pie">
            {(() => {
              const PAYER_PALETTE = [
                '#3F6E60', '#B08A3A', '#5B4A7A', '#2D4E6E', '#8A3A3A',
                '#4F7F7A', '#C77D4A', '#6E5B8A', '#4A6B3F', '#8A6FA3',
                '#9F6A2F', '#555E7B', '#6F8A3A', '#6A3A5F', '#3A6B8A',
              ];
              const sorted = [...payerDist].sort((a, b) => b.collected - a.collected);
              const maxV = Math.max(...sorted.map(p => p.collected), 1);
              return (
                <div className="space-y-2.5" data-testid="bi-payer-bars">
                  {sorted.map((p, i) => {
                    const pct = p.share * 100;
                    const w = (p.collected / maxV) * 100;
                    const color = PAYER_PALETTE[i % PAYER_PALETTE.length];
                    return (
                      <div key={p.payer} className="group" data-testid={`payer-bar-${p.payer.replace(/\W+/g,'_')}`}>
                        <div className="flex items-baseline justify-between mb-0.5">
                          <div className="flex items-center gap-2 text-[12px]" style={{ color: C.ink }}>
                            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                            <span className="font-medium truncate max-w-[220px]" title={p.payer}>{p.payer}</span>
                          </div>
                          <div className="text-[11px] tabular-nums" style={{ color: C.soft }}>
                            <span className="font-semibold mr-2" style={{ color: C.ink }}>{CURRENCY(p.collected)}</span>
                            <span>{pct.toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#F2EEF4' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(w, 1)}%`, background: color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Card>
          <Card className="lg:col-span-2 p-5 rounded-2xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: C.soft, borderBottom: `1px solid ${C.border}` }}>
                  <th className="py-2">Payer</th>
                  <th className="py-2 text-right">Collected</th>
                  <th className="py-2 text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {payerDist.map(p => (
                  <tr key={p.payer} className="border-b" style={{ borderColor: C.hair }} data-testid={`payer-row-${p.payer.replace(/\W+/g,'_')}`}>
                    <td className="py-2 truncate max-w-[220px]" title={p.payer}>{p.payer}</td>
                    <td className="py-2 text-right font-medium">{CURRENCY(p.collected)}</td>
                    <td className="py-2 text-right">{(p.share * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </section>

      {/* ── 3. Payer scorecard — behaviour per payer ────────────── */}
      <section className="mt-10" data-testid="bi-payers-section-scorecard">
        <SectionTitle title="Payer scorecard — realization & denial at a glance" subtitle="Each card shows what share of revenue the payer brings, how much of their contracted $ actually lands, and how often they deny · best health first" />
        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-3 mt-4" data-testid="bi-payer-scorecard">
          {(() => {
            const CAP = (v) => Math.max(0, Math.min(1, Number(v) || 0));
            const top = [...rows].sort((a, b) => (b.collected || 0) - (a.collected || 0)).slice(0, 8);
            const totalCollected = rows.reduce((s, r) => s + (r.collected || 0), 0) || 1;
            return top.map((p, i) => {
              const share = (p.collected || 0) / totalCollected;
              const real  = CAP(p.realization);
              const deny  = CAP(p.denial_rate);
              let health = 'Healthy', healthBg = '#EEF4F2', healthFg = '#3F7D6E';
              if (real < 0.70 || deny > 0.10) { health = 'Watch';  healthBg = '#FBEDED'; healthFg = '#8A3A3A'; }
              else if (real < 0.85 || deny > 0.05) { health = 'OK'; healthBg = '#FCF6E7'; healthFg = '#8A6A1F'; }
              const realColor = real >= 0.85 ? '#3F7D6E' : real >= 0.70 ? '#B08A3A' : '#8A3A3A';
              const denyColor = deny <= 0.05 ? '#3F7D6E' : deny <= 0.10 ? '#B08A3A' : '#8A3A3A';
              return (
                <Card
                  key={p.payer}
                  className="p-4 rounded-2xl bg-white border"
                  style={{ borderColor: C.border }}
                  data-testid={`bi-payer-card-${p.payer.replace(/\W+/g,'_')}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-[13px] leading-tight truncate" style={{ color: C.ink }} title={p.payer}>
                        <span className="mr-1.5 text-[11px] tabular-nums" style={{ color: C.soft }}>#{i + 1}</span>
                        {p.payer}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.soft }}>
                        {CURRENCY_SHORT(p.collected)} · <span style={{ color: C.ink2 }}>{(share * 100).toFixed(1)}% of revenue</span>
                      </div>
                    </div>
                    <span
                      className="text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap"
                      style={{ background: healthBg, color: healthFg }}
                      data-testid={`bi-payer-health-${p.payer.replace(/\W+/g,'_')}`}
                    >
                      {health}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2.5">
                    <div>
                      <div className="flex items-baseline justify-between text-[10.5px]">
                        <span style={{ color: C.soft }}>Realization</span>
                        <span className="font-semibold tabular-nums" style={{ color: realColor }}>{(real * 100).toFixed(1)}%</span>
                      </div>
                      <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: '#F2EEF4' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${real * 100}%`, background: realColor }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between text-[10.5px]">
                        <span style={{ color: C.soft }}>Denial rate</span>
                        <span className="font-semibold tabular-nums" style={{ color: denyColor }}>{(deny * 100).toFixed(1)}%</span>
                      </div>
                      <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: '#F2EEF4' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(deny * 500, 100)}%`, background: denyColor }} />
                      </div>
                      <div className="text-[9.5px] mt-0.5" style={{ color: C.soft }}>
                        scale: 0–20% (bar tops out at 20% denial)
                      </div>
                    </div>
                  </div>
                </Card>
              );
            });
          })()}
        </div>
        <div className="mt-3 text-[11px] flex flex-wrap items-center gap-3" style={{ color: C.soft }}>
          <span className="font-semibold uppercase tracking-widest text-[10px]">Health scoring:</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#EEF4F2' }} /><b style={{ color: '#3F7D6E' }}>Healthy</b> ≥85% realization & &lt;5% denial</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#FCF6E7' }} /><b style={{ color: '#8A6A1F' }}>OK</b> 70–85% realization or 5–10% denial</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#FBEDED' }} /><b style={{ color: '#8A3A3A' }}>Watch</b> &lt;70% realization or &gt;10% denial</span>
        </div>
      </section>

      {/* ── 4. Underpayments recovery queue (if any) ────────────── */}
      {up && up.count > 0 && (
        <section className="mt-10" data-testid="bi-payers-section-underpayments">
          <SectionTitle title="Underpayments · closed claims paying below 90% of contract"
                        subtitle={`${up.count.toLocaleString()} of ${up.closed_claims.toLocaleString()} closed claims · $${(up.amount||0).toLocaleString(undefined,{maximumFractionDigits:0})} owed`} />
          <div className="grid lg:grid-cols-3 gap-4 mt-4">
            <Card className="p-5 rounded-2xl bg-white border lg:col-span-1" style={{ borderColor: C.border }} data-testid="bi-underpaid-by-payer">
              <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: C.soft }}>Worst offenders</div>
              <div className="space-y-2">
                {(up.by_payer || []).slice(0, 6).map((p) => (
                  <div key={p.payer} className="flex items-center justify-between text-[12px]">
                    <div style={{ color: C.ink }}>{p.payer}</div>
                    <div className="text-right">
                      <div className="font-serif text-[14px]" style={{ color: C.rose }}>{CURRENCY(p.amount)}</div>
                      <div className="text-[10px]" style={{ color: C.soft }}>{p.count} claim{p.count===1?'':'s'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="rounded-2xl overflow-hidden bg-white border lg:col-span-2" style={{ borderColor: C.border }}>
              <div className="px-5 py-3 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft, background: C.paper }}>Top 20 individual underpayments</div>
              <div className="overflow-auto max-h-[380px]">
                <table className="w-full text-[12px]" data-testid="bi-underpaid-rows">
                  <thead className="text-[9px] uppercase tracking-wider sticky top-0" style={{ background: C.paper, color: C.soft }}>
                    <tr>
                      <th className="text-left py-2 px-3">Client</th>
                      <th className="text-left py-2 px-3">DOS</th>
                      <th className="text-left py-2 px-3">Payer</th>
                      <th className="text-left py-2 px-3">CPT</th>
                      <th className="text-right py-2 px-3">Expected</th>
                      <th className="text-right py-2 px-3">Collected</th>
                      <th className="text-right py-2 px-3">Shortfall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(up.top_rows || []).map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: C.hair }}>
                        <td className="py-2 px-3" style={{ color: C.ink }}>{r.client}</td>
                        <td className="py-2 px-3" style={{ color: C.ink2 }}>{r.dos}</td>
                        <td className="py-2 px-3" style={{ color: C.ink2 }}>{r.payer_group}</td>
                        <td className="py-2 px-3 tabular-nums" style={{ color: C.ink2 }}>{r.code}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{CURRENCY(r.expected)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{CURRENCY(r.collected)}</td>
                        <td className="py-2 px-3 text-right font-semibold" style={{ color: C.rose }}>{CURRENCY(r.underpaid_by)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* ── 5. Full detail table — power users ──────────────────── */}
      <section className="mt-10" data-testid="bi-payers-section-detail">
        <SectionTitle title="Payer intelligence · full detail" subtitle="Volume · realization · denial rate · contract gap — the full table backing every tile above" />
        <Card className="rounded-2xl overflow-hidden bg-white border mt-4" style={{ borderColor: C.border }}>
          <div className="overflow-auto">
            <table className="w-full text-[13px]" data-testid="bi-payer-intel">
              <thead className="text-[10px] uppercase tracking-wider" style={{ background: C.paper, color: C.soft }}>
                <tr>
                  <th className="text-left py-3 px-4">Payer</th>
                  <th className="text-right py-3 px-4">Collected</th>
                  <th className="text-right py-3 px-4">Expected</th>
                  <th className="text-right py-3 px-4">Realization</th>
                  <th className="text-right py-3 px-4">Volume Share</th>
                  <th className="text-right py-3 px-4">Denial Rate</th>
                  <th className="text-right py-3 px-4">Contract Gap</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => {
                  const realCol = p.realization >= 1 ? C.teal : p.realization >= 0.9 ? C.amber : C.rose;
                  const denCol  = p.denial_rate  < 0.03 ? C.teal : p.denial_rate < 0.07 ? C.amber : C.rose;
                  return (
                    <tr key={i} className="border-t" style={{ borderColor: C.hair }}>
                      <td className="py-3 px-4 font-medium" style={{ color: C.ink }}>{p.payer}</td>
                      <td className="py-3 px-4 text-right font-serif" style={{ color: C.purple }}>{CURRENCY(p.collected)}</td>
                      <td className="py-3 px-4 text-right" style={{ color: C.ink2 }}>{CURRENCY(p.expected)}</td>
                      <td className="py-3 px-4 text-right font-semibold" style={{ color: realCol }}>{PCT(p.realization)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ background: C.hair }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(100, p.volume_share * 100)}%`, background: C.gold }} />
                          </div>
                          <span className="tabular-nums">{PCT(p.volume_share)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold" style={{ color: denCol }}>{PCT(p.denial_rate)}</td>
                      <td className="py-3 px-4 text-right font-semibold" style={{ color: p.gap > 0 ? C.rose : C.soft }}>{CURRENCY(p.gap)}</td>
                    </tr>
                  );
                })}
                {!rows.length && <tr><td colSpan={7} className="text-center py-8" style={{ color: C.soft }}>No payer data yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* ── 6. Family reconciliation — deepest drill-down ───────── */}
      <section className="mt-10" data-testid="bi-payers-section-family">
        <PayerFamilyRollup rows={rows} />
      </section>
    </>
  );
}

// ─── Clinicians tab ───
function CliniciansTab({ d }) {
  const rows = d.provider_leaderboard || d.top_providers || [];
  const avg = d.kpis?.avg_provider_show_rate || 0;
  return (
    <>
      <SectionTitle title="Clinician leaderboard" subtitle={`Top revenue producers · avg show rate ${PCT(avg)}`} />
      <div className="grid lg:grid-cols-3 gap-3 mb-6">
        {rows.slice(0, 3).map((p, i) => (
          <Card key={i} className="p-5 rounded-2xl bg-white border relative overflow-hidden" style={{ borderColor: C.border }}>
            <div className="absolute -top-6 -right-6 opacity-5" style={{ fontSize: 130 }}>#{i + 1}</div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: i === 0 ? C.gold : C.soft }}>
              <Trophy size={12} strokeWidth={2.2} /> Rank #{i + 1}
            </div>
            <div className="font-serif text-lg mt-2" style={{ color: C.ink }}>{p.provider}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Collected</div>
                <div className="font-serif text-2xl mt-1" style={{ color: C.purple }}>{CURRENCY_SHORT(p.collected)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Show Rate</div>
                <div className="font-serif text-2xl mt-1" style={{ color: p.show_rate >= 0.90 ? C.teal : p.show_rate >= 0.80 ? C.amber : C.rose }}>{PCT(p.show_rate)}</div>
              </div>
            </div>
            <div className="mt-3 text-[11px]" style={{ color: C.soft }}>
              {p.scheduled} scheduled · {p.shows} shows
              {p.show_delta_vs_avg != null && (
                <span className={`ml-2 font-semibold ${p.show_delta_vs_avg >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
                  {p.show_delta_vs_avg >= 0 ? '↑' : '↓'} {Math.abs(p.show_delta_vs_avg * 100).toFixed(1)}pt vs avg
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl overflow-hidden bg-white border" style={{ borderColor: C.border }}>
        <div className="overflow-auto">
          <table className="w-full text-[13px]" data-testid="bi-clinician-leaderboard">
            <thead className="text-[10px] uppercase tracking-wider" style={{ background: C.paper, color: C.soft }}>
              <tr>
                <th className="text-left  py-3 px-4">Rank</th>
                <th className="text-left  py-3 px-4">Clinician</th>
                <th className="text-right py-3 px-4">Collected</th>
                <th className="text-right py-3 px-4">Scheduled</th>
                <th className="text-right py-3 px-4">Shows</th>
                <th className="text-right py-3 px-4">Show Rate</th>
                <th className="text-right py-3 px-4">Δ vs Avg</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const showCol = p.show_rate >= 0.90 ? C.teal : p.show_rate >= 0.80 ? C.amber : C.rose;
                return (
                  <tr key={i} className="border-t" style={{ borderColor: C.hair }}>
                    <td className="py-3 px-4 font-serif" style={{ color: C.soft }}>#{i + 1}</td>
                    <td className="py-3 px-4 font-medium" style={{ color: C.ink }}>{p.provider}</td>
                    <td className="py-3 px-4 text-right font-serif" style={{ color: C.purple }}>{CURRENCY(p.collected)}</td>
                    <td className="py-3 px-4 text-right">{p.scheduled}</td>
                    <td className="py-3 px-4 text-right">{p.shows}</td>
                    <td className="py-3 px-4 text-right font-semibold" style={{ color: showCol }}>{PCT(p.show_rate)}</td>
                    <td className="py-3 px-4 text-right text-[11px]" style={{ color: (p.show_delta_vs_avg || 0) >= 0 ? C.teal : C.rose }}>
                      {p.show_delta_vs_avg != null ? `${(p.show_delta_vs_avg * 100).toFixed(1)}pt` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ─── Clinics tab ───
function ClinicsTab({ d }) {
  // Memoize the filtered subset so we don't re-scan the clinic list on every
  // re-render (parent's date-range picker + tab switches trigger renders here).
  const rows = useMemo(() => d.clinic_scorecard || [], [d.clinic_scorecard]);
  const visibleRows = useMemo(() => rows.filter(c => c.collected > 0), [rows]);
  return (
    <>
      {(d.utilization || d.od_bonus) && (
        <>
          <SectionTitle title="Utilization & OD bonus" subtitle="Group + per clinic — the two numbers that drive Monday's meeting" />
          <div className="grid xl:grid-cols-2 gap-4 mb-6">
            {d.utilization && <UtilizationCard u={d.utilization} />}
            {d.od_bonus     && <BonusMeterCard  b={d.od_bonus} />}
          </div>
        </>
      )}
      <SectionTitle title="Clinic scorecard" subtitle="Ranked by collected revenue with actionable alerts" />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {visibleRows.map((c) => (
          <Card key={c.clinic} data-testid={`bi-clinic-card-${c.clinic.replace(/\s+/g, '-').toLowerCase()}`}
                className="p-5 rounded-2xl bg-white border relative overflow-hidden" style={{ borderColor: C.border }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.gold }}>
                  <Trophy size={11} /> Rank #{c.rank}
                </div>
                <div className="font-serif text-xl mt-1" style={{ color: C.ink }}>{c.clinic}</div>
              </div>
              {c.mom_delta != null && (
                <div className={`text-[11px] font-semibold flex items-center gap-0.5 ${c.mom_delta >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
                  {c.mom_delta >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                  {Math.abs(c.mom_delta * 100).toFixed(1)}% MoM
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Collected</div>
                <div className="font-serif text-lg mt-0.5" style={{ color: C.purple }}>{CURRENCY_SHORT(c.collected)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Show</div>
                <div className="font-serif text-lg mt-0.5" style={{ color: c.show_rate >= 0.90 ? C.teal : c.show_rate >= 0.80 ? C.amber : C.rose }}>{PCT(c.show_rate)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Lost</div>
                <div className="font-serif text-lg mt-0.5" style={{ color: C.rose }}>{CURRENCY_SHORT(c.lost)}</div>
              </div>
            </div>

            {/* mini sparkline of collected */}
            <div className="mt-3 h-10 -mx-2">
              <Sparkline data={c.series} dataKey="collected" color={C.purple} height={40} />
            </div>

            {c.alerts?.length > 0 && (
              <div className="mt-3 space-y-1">
                {c.alerts.map((a, i) => (
                  <div key={i} className="text-[11px] flex items-center gap-1.5" style={{ color: a.kind === 'danger' ? C.rose : C.amber }}>
                    <AlertTriangle size={11} /> {a.label}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <SectionTitle title="Full scorecard" subtitle="All metrics for every clinic" />
      <Card className="rounded-2xl overflow-hidden bg-white border" style={{ borderColor: C.border }}>
        <div className="overflow-auto">
          <table className="w-full text-[13px]" data-testid="bi-clinic-scorecard">
            <thead className="text-[10px] uppercase tracking-wider" style={{ background: C.paper, color: C.soft }}>
              <tr>
                <th className="text-left  py-3 px-4">#</th>
                <th className="text-left  py-3 px-4">Clinic</th>
                <th className="text-right py-3 px-4">Scheduled</th>
                <th className="text-right py-3 px-4">Shows</th>
                <th className="text-right py-3 px-4">Show %</th>
                <th className="text-right py-3 px-4">Cancels</th>
                <th className="text-right py-3 px-4">No-Shows</th>
                <th className="text-right py-3 px-4">Collected</th>
                <th className="text-right py-3 px-4">Expected</th>
                <th className="text-right py-3 px-4">Realization</th>
                <th className="text-right py-3 px-4">Lost $</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.clinic} className="border-t" style={{ borderColor: C.hair }}>
                  <td className="py-3 px-4 font-serif" style={{ color: C.soft }}>#{c.rank}</td>
                  <td className="py-3 px-4 font-medium" style={{ color: C.ink }}>{c.clinic}</td>
                  <td className="py-3 px-4 text-right">{NUM(c.scheduled)}</td>
                  <td className="py-3 px-4 text-right">{NUM(c.shows)}</td>
                  <td className="py-3 px-4 text-right font-semibold" style={{ color: c.show_rate >= 0.90 ? C.teal : c.show_rate >= 0.80 ? C.amber : C.rose }}>{PCT(c.show_rate)}</td>
                  <td className="py-3 px-4 text-right">{NUM(c.cancels)}</td>
                  <td className="py-3 px-4 text-right">{NUM(c.no_shows)}</td>
                  <td className="py-3 px-4 text-right font-serif" style={{ color: C.purple }}>{CURRENCY(c.collected)}</td>
                  <td className="py-3 px-4 text-right">{CURRENCY(c.expected)}</td>
                  <td className="py-3 px-4 text-right font-semibold">{PCT(c.realization)}</td>
                  <td className="py-3 px-4 text-right font-semibold" style={{ color: C.rose }}>{CURRENCY(c.lost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ─── Definitions & Data Health tab ───────────────────────────────────
// Executive-BI-only definitions: business question, formula, numerator,
// denominator, units, date basis, direction of improvement, known
// limitations, and reconciliation notes. Sourced entirely from the
// existing shared snapshot — no DB writes, no shared calc changes.
const METRIC_DEFS = [
  {
    section: 'Financial · receipt-basis',
    metrics: [
      { name: 'Collected Less Fees (YTD)', unit: '$', question: 'Cash received net of processing fees this year.',
        formula: 'SUM(Income & Fees · Net) over YTD months present',
        numerator: 'Income & Fees · Net (already gross minus processing fees)',
        denominator: '—', date_basis: 'Deposit / receipt month (Income & Fees export)',
        direction: '▲ higher is better',
        limits: 'This is NOT operating profit. Payroll, rent, and other operating costs are outside the Income & Fees report and are not netted here.' },
      { name: 'Gross Income (YTD)', unit: '$', question: 'Cash received before processing fees.',
        formula: 'SUM(Income & Fees · Gross) over YTD months present',
        numerator: 'Income & Fees · Gross', denominator: '—',
        date_basis: 'Deposit / receipt month',
        direction: '▲ higher is better',
        limits: 'Receipts, not accrual. Refunds appear negative in the month applied.' },
      { name: 'Total Collected (Income & Fees · deposit-basis)', unit: '$', question: 'Cash actually deposited across all payers this period.',
        formula: 'SUM(client_pay + ins_pay) from Income & Fees for months in scope (equivalent to kpis.gross_income_ytd)',
        numerator: 'client_pay + ins_pay from Income & Fees',
        denominator: '—',
        date_basis: 'Deposit month (Income & Fees export)',
        direction: '▲ higher is better',
        limits: 'Deposits, not DOS. When Appointment-Status posting lags or misfires (rate mismatches, unposted payments), the payer-level sum of appt_status collected will diverge — a "posting drift" banner surfaces in Financial & RCM whenever the gap is ≥2%. Use this KPI for cash totals; keep Collected (DOS) for tying revenue back to individual visits.' },
      { name: 'Collected (DOS)', unit: '$', question: 'Cash allocated back to service dates this period.',
        formula: 'SUM(client_paid + insurance_paid) over Appointment-Status rows in period',
        numerator: 'client_paid + insurance_paid', denominator: '—',
        date_basis: 'Date of service (DOS)',
        direction: '▲ higher is better',
        limits: 'Different denominator from Collected Less Fees. Use DOS when tying revenue to a visit; use Collected Less Fees when tying to a deposit month.' },
      { name: 'Rev / Session', unit: '$/appt',
        question: 'Average cash per attended appointment.',
        formula: 'Collected (DOS) ÷ Shows (attendance count where status = Show)',
        numerator: 'Collected (DOS)', denominator: 'Shows (count)',
        date_basis: 'Date of service',
        direction: '▲ higher is better',
        limits: 'Not adjusted for payer mix or CPT mix. Use CPT × payer detail for yield.' },
      { name: 'Margin / Session (payroll-basis)', unit: '$/appt',
        question: 'Contribution margin per attended appointment after clinician pay.',
        formula: '(Income per session cents − Payroll per session cents) ÷ 100, latest finalized payroll period',
        numerator: 'Income − Payroll cents on the payroll period',
        denominator: 'Sessions on the payroll period',
        date_basis: 'Payroll period end date',
        direction: '▲ higher is better',
        limits: 'Payroll-only. Rent, admin, benefits, and non-clinical costs are excluded. Comparator = simple average of the 3 finalized periods immediately preceding the latest.' },
    ],
  },
  {
    section: 'Operations · attendance',
    metrics: [
      { name: 'Appointments Billed', unit: 'count',
        question: 'How many distinct appointments were on Appointment Status this period.',
        formula: 'COUNT DISTINCT Appointment-Status rows (client + DOS + provider + code + seq)',
        numerator: 'Rows in bi_appt_status', denominator: '—',
        date_basis: 'Date of service',
        direction: 'informational',
        limits: 'A CPT-line breakdown may report a HIGHER total when a single appointment bills multiple CPT codes — that is expected, not a defect.' },
      { name: 'Show Rate', unit: '%',
        question: 'Of scheduled visits, what share were actually attended.',
        formula: 'Shows ÷ Scheduled',
        numerator: 'attendance rows with status = Show',
        denominator: 'attendance rows total (Show + Client cancels + No-shows + Clinician cancels)',
        date_basis: 'Date of service',
        direction: '▲ higher is better',
        limits: 'Clinician cancellations are kept in their own category and reported alongside — they are NOT rolled into client attendance figures.' },
      { name: 'Client Cancels', unit: 'count',
        question: 'How many appointments the client cancelled or late-cancelled.',
        formula: 'COUNT(status ∈ {Canceled, Late canceled})',
        numerator: 'Attendance rows with those statuses', denominator: '—',
        date_basis: 'Date of service', direction: '▼ lower is better',
        limits: 'Late cancels are still countable slots but were too late to re-book.' },
      { name: 'Clinician Cancels', unit: 'count',
        question: 'How many appointments were cancelled by the clinician.',
        formula: 'COUNT(status = Clinician canceled)',
        numerator: 'Attendance rows with that status', denominator: '—',
        date_basis: 'Date of service', direction: '▼ lower is better',
        limits: 'Reported alongside client cancels but never combined into a single attendance measure. Reallocated to home clinic for Telehealth appointments.' },
    ],
  },
  {
    section: 'Payer · realization',
    metrics: [
      { name: 'Realization (payer)', unit: '%',
        question: 'What share of expected contract dollars did this payer actually pay us.',
        formula: 'SUM(collected) ÷ SUM(expected), aggregated across appointments in scope',
        numerator: 'SUM(client_paid + ins_paid)',
        denominator: 'SUM(contract rate × units) — rows without a matching rate are excluded from the denominator and reported separately as missing_rate',
        date_basis: 'Date of service',
        direction: '▲ closer to 100% is better',
        limits: 'Values above 100% are possible and legitimate — usually scope, expected-amount input, or claim maturity (adjustments received later than the current cutoff). NOT auto-flagged. Investigate scope/inputs when reviewing.' },
      { name: 'Contract Gap', unit: '$',
        question: 'How much less we collected than expected.',
        formula: 'max(SUM(expected) − SUM(collected), 0)',
        numerator: 'SUM(expected − collected) where positive per payer',
        denominator: '—',
        date_basis: 'Date of service',
        direction: '▼ lower is better',
        limits: 'Aggregate positive-gap total can coexist with $0 closed underpayments — they are different populations (all appts vs closed-claim underpayments) and are NOT forced to reconcile.' },
      { name: 'Denial Rate', unit: '%',
        question: 'Of claims filed, what share came back denied.',
        formula: 'Denied ÷ Total claims (Claims export)',
        numerator: 'Claims with denied status', denominator: 'All claims in scope',
        date_basis: 'Claim date',
        direction: '▼ lower is better',
        limits: 'First-pass reason detail requires the Claims export to carry denial reasons — currently unavailable in the ingested feed.' },
    ],
  },
  {
    section: 'Utilization · OD Bonus',
    metrics: [
      { name: 'Group Utilization', unit: '%',
        question: 'Are we hitting the monthly show target across clinics, pace-matched to today.',
        formula: 'SUM(Shows current month across active clinics) ÷ (720 × biz-days-elapsed ÷ biz-days-in-month × active clinics)',
        numerator: 'Shows current month across active clinics',
        denominator: 'Pace-prorated monthly target (720 shows/clinic/month × BD-elapsed/BD-total × N clinics)',
        date_basis: 'Date of service · pace anchor uses biz_today() (America/Chicago)',
        direction: '▲ higher is better',
        limits: 'Utilization pace uses supported cutoff (business days elapsed) — not the browser wall-clock. Refreshing on the same day should yield the same pace against unchanged data.' },
      { name: 'OD Bonus Progress', unit: '%',
        question: 'How each clinic is tracking to the quarterly bonus target.',
        formula: 'Quarter-to-date bonus-counted sessions ÷ pace target',
        numerator: 'Sessions where status ∈ {Show, No show, Late canceled} + OD-performed 92523 evals',
        denominator: 'Quarterly bonus target (2,225) × BD-elapsed ÷ BD-total-in-quarter',
        date_basis: 'Date of service · quarter anchor uses biz_today()',
        direction: '▲ higher is better',
        limits: 'Streaks (consecutive-week under-target) and cumulative shortfall are DISTINCT measures. A "4 weeks in a row" list must qualify each week against the target — three of four below target is a "3 of 4" flag, not a 4-week streak.' },
    ],
  },
];

function DefinitionsSection({ d, status, validation, range, throughDate }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return METRIC_DEFS;
    return METRIC_DEFS.map(sec => ({
      ...sec,
      metrics: sec.metrics.filter(m =>
        m.name.toLowerCase().includes(s)
        || (m.question || '').toLowerCase().includes(s)
        || (m.formula || '').toLowerCase().includes(s)),
    })).filter(sec => sec.metrics.length);
  }, [q]);
  const freshness = d?.data_freshness || {};
  const counts = status?.counts || d?.counts || {};
  const rangeLabel = range?.from || range?.to
    ? `${range?.from || '…'} → ${range?.to || '…'}`
    : 'All time';
  return (
    <>
      {/* Scope bar */}
      <Card className="p-4 rounded-2xl mb-5 bg-white border" style={{ borderColor: C.border }} data-testid="bi-scope-bar">
        <div className="flex flex-wrap items-center gap-4 text-[11px]" style={{ color: C.soft }}>
          <span><span style={{ color: C.ink2, fontWeight: 700 }}>Scope</span> · {rangeLabel}</span>
          <span><span style={{ color: C.ink2, fontWeight: 700 }}>Data through</span> · {throughDate}</span>
          <span><span style={{ color: C.ink2, fontWeight: 700 }}>Comparison basis</span> · Prior period, business-day pace-matched. Suppressed until ≥5 biz days elapsed.</span>
          <span><span style={{ color: C.ink2, fontWeight: 700 }}>Timezone</span> · America/Chicago</span>
          {validation?.status && !validation.never_run && (
            <span style={{ color: validation.status === 'PASS' ? C.teal : C.rose, fontWeight: 700 }}>Daily validation · {validation.status}</span>
          )}
        </div>
      </Card>

      {/* Source coverage */}
      <SectionTitle title="Source coverage & freshness" subtitle="Row counts and latest DOS per BI feed. Stale collection = the KPIs derived from it are stale." />
      <Card className="p-5 rounded-2xl mb-6 bg-white border overflow-hidden" style={{ borderColor: C.border }} data-testid="bi-source-coverage">
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase tracking-wider" style={{ color: C.soft, borderBottom: `1px solid ${C.border}` }}>
            <tr>
              <th className="text-left py-2 px-2">Feed</th>
              <th className="text-right py-2 px-2">Rows</th>
              <th className="text-right py-2 px-2">Latest DOS</th>
              <th className="text-left py-2 px-2">Powers</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'attendance',  label: 'Attendance',           powers: 'Shows, cancels, no-shows, show rate, utilization, OD bonus' },
              { key: 'appt_status', label: 'Appointment Status',   powers: 'Collected (DOS), realization, Rev/Session, CPT × payer detail' },
              { key: 'claims',      label: 'Claims',               powers: 'Denial rate, aging, pending A/R (weighted by expected)' },
              { key: 'income',      label: 'Income & Fees',        powers: 'Gross, Collected Less Fees (YTD)' },
              { key: 'payer_alloc', label: 'Payer Allocation',     powers: 'Payer-family normalization' },
            ].map(row => {
              const f = freshness[row.key] || {};
              const rows = counts[`bi_${row.key}`] ?? f.rows ?? null;
              return (
                <tr key={row.key} className="border-t" style={{ borderColor: C.hair }} data-testid={`bi-src-row-${row.key}`}>
                  <td className="py-2 px-2 font-medium" style={{ color: C.ink }}>{row.label}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{rows != null ? NUM(rows) : '—'}</td>
                  <td className="py-2 px-2 text-right" style={{ color: C.ink2 }}>{f.latest_dos ? fmtDate(f.latest_dos) : '—'}</td>
                  <td className="py-2 px-2" style={{ color: C.soft }}>{row.powers}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Known limitations for currently unavailable KPIs */}
      <SectionTitle title="Requested KPIs with dependency state" subtitle="Where a metric is requested but the underlying data isn't wired, we surface it here honestly — no mock numbers appear elsewhere on the dashboard." />
      <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="bi-dependency-state">
        <div className="grid md:grid-cols-2 gap-3 text-[12px]">
          {[
            { name: 'Operating profit / margin', state: 'Unavailable', why: 'Requires rent, admin, benefits, and other operating cost feeds beyond payroll. Not currently ingested.' },
            { name: 'Cash / operating cash flow', state: 'Unavailable', why: 'Requires bank / cash movement feed. Not currently ingested.' },
            { name: 'AR aging by dollars (paid-basis)', state: 'Partial', why: 'Pending claims aged by dollars use SUM(expected) as a proxy — Claims export does not carry paid amounts per line.' },
            { name: 'Denial reason Pareto', state: 'Unavailable', why: 'Claims export does not currently ingest denial-reason codes.' },
            { name: 'Waitlist / time-to-access including families still waiting', state: 'Partial', why: 'Inquiries pipeline captures leads but access-time end event (first show) is available; families still waiting are derivable from Inquiries but not surfaced here yet.' },
            { name: 'Documentation overdue rate', state: 'Available in Pending Notes report', why: 'Report exists elsewhere in the app; not duplicated on Executive BI. Link out preserved.' },
            { name: 'Authorization remaining units', state: 'Unavailable', why: 'Auth-tracking feed is not ingested to BI. Available inside client detail.' },
            { name: 'Clinical outcomes / experience', state: 'Unavailable', why: 'Outcome measure export is not ingested. No synthetic quality score is displayed.' },
          ].map(row => (
            <div key={row.name} className="rounded-xl border p-3" style={{ borderColor: C.border, background: '#FBF8F4' }}>
              <div className="flex items-center justify-between">
                <div style={{ color: C.ink, fontWeight: 600 }}>{row.name}</div>
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: row.state === 'Unavailable' ? '#F5EAEA' : row.state === 'Partial' ? '#F7EFDA' : '#EAF2EE',
                               color: row.state === 'Unavailable' ? C.rose : row.state === 'Partial' ? C.amber : C.teal }}>
                  {row.state}
                </span>
              </div>
              <div className="text-[11px] mt-1" style={{ color: C.soft }}>{row.why}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Metric contracts */}
      <SectionTitle title="Metric definitions" subtitle="One contract per metric — business question, formula, numerator, denominator, date basis, direction, and known limitations."
        right={
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search metric…"
                 data-testid="bi-defs-search"
                 className="text-sm rounded-full border px-3 py-1.5"
                 style={{ borderColor: C.border, background: 'white', color: C.ink2, minWidth: 220 }} />
        } />
      <div className="space-y-4 mb-8">
        {filtered.map(sec => (
          <Card key={sec.section} className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid={`bi-defs-section-${sec.section.replace(/\W+/g,'-').toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>{sec.section}</div>
            <div className="space-y-4">
              {sec.metrics.map(m => (
                <div key={m.name} data-testid={`bi-def-${m.name.replace(/\W+/g,'-').toLowerCase()}`} className="rounded-xl border p-4" style={{ borderColor: C.border, background: '#FBF8F4' }}>
                  <div className="flex flex-wrap items-baseline gap-2 justify-between">
                    <div className="font-serif text-[16px]" style={{ color: C.ink }}>{m.name}</div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>
                      <span>Unit · <span style={{ color: C.ink2, fontWeight: 700 }}>{m.unit}</span></span>
                      <span>· {m.direction}</span>
                    </div>
                  </div>
                  <div className="text-[12px] mt-1" style={{ color: C.ink2 }}>{m.question}</div>
                  <div className="mt-2 grid md:grid-cols-2 gap-2 text-[11px]" style={{ color: C.soft }}>
                    <div><span style={{ color: C.ink2, fontWeight: 700 }}>Formula:</span> {m.formula}</div>
                    <div><span style={{ color: C.ink2, fontWeight: 700 }}>Date basis:</span> {m.date_basis}</div>
                    <div><span style={{ color: C.ink2, fontWeight: 700 }}>Numerator:</span> {m.numerator}</div>
                    <div><span style={{ color: C.ink2, fontWeight: 700 }}>Denominator:</span> {m.denominator}</div>
                  </div>
                  {m.limits && (
                    <div className="mt-2 text-[11px] flex items-start gap-1.5" style={{ color: C.amber }}>
                      <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                      <span><span style={{ fontWeight: 700 }}>Known limits:</span> {m.limits}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Boundary attestation */}
      <Card className="p-4 rounded-2xl mb-8 bg-white border" style={{ borderColor: C.border }} data-testid="bi-boundary-attestation">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.teal }}>Boundary attestation</div>
        <ul className="text-[12px] space-y-1" style={{ color: C.ink2 }}>
          <li>· Executive BI derivations are read-only over the existing snapshot. No DB writes, no schema changes, no shared calculation edits.</li>
          <li>· Contract Rates page/button/behavior preserved exactly. Executive BI links out; it never edits rates.</li>
          <li>· Other reports (Dashboard, Payroll, OD Digest, Underpayment, Pending Notes) consume the same shared functions — this tab does not alter them.</li>
        </ul>
      </Card>
    </>
  );
}


// MM-DD-YYYY formatter (per Feb 2026 spec: all referral dates render long-form).
function fmtDateLong(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : String(iso);
}

const BUCKET_COLORS = { Google: C.gold, Website: C.teal, Fax: C.purple, Other: C.rose };

function WaitlistSection() {
  const [wl, setWl] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get('/exec-bi/waitlist')
      .then(r => { if (alive) setWl(r.data); })
      .catch(() => { if (alive) setWl(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  if (loading) return <div className="p-6 text-center text-[12px]" style={{ color: C.soft }}>Loading waitlist…</div>;
  if (!wl) return null;
  const w = wl.waitlist || {};
  const a = wl.access_time || {};
  const dayLabel = (n) => n == null ? '—' : `${n} ${n === 1 ? 'day' : 'days'}`;
  const bucketMax = Math.max(1, ...(w.buckets || []).map(b => b.count));
  return (
    <>
      <SectionTitle title="Waitlist & Time to Access"
        subtitle="Mirrors the Inquiries page: `Needs Follow-up` = new + contacted. The active follow-up count below matches contacted leads that are stale (≥1 business day since the last touch) — those actually waiting on us."
        right={w.oldest_new_received_iso ? <span className="text-[11px]" style={{ color: C.soft }}>Oldest new lead: {fmtDate(w.oldest_new_received_iso)}</span> : null}
      />
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile testid="bi-waitlist-active-followup" label="Active follow-up · contacted & stale" value={NUM(w.active_followup_count || 0)} sub={`Reached out ≥1 biz day ago, no reply yet · ${NUM(w.contacted_total || 0)} contacted total`} icon={AlertTriangle} tone={C.rose} />
        <StatTile testid="bi-waitlist-new"             label="Awaiting first touch · new"       value={NUM(w.awaiting_first_touch || 0)} sub="Auto-imported leads not yet dispositioned" icon={Inbox} tone={C.purple} />
        <StatTile testid="bi-waitlist-intake"          label="Intake in progress"               value={NUM(w.intake_in_progress || 0)} sub="Converted · no eval date yet" icon={Users} tone={C.amber} />
        <StatTile testid="bi-waitlist-cohort"          label={`Access-time cohort (${a.cohort_window_days || 90}d)`} value={NUM(a.cohort_size || 0)} sub="Clients with both referral and eval dates" icon={CalendarCheck} tone={C.teal} />
      </div>

      {/* Active follow-up table — the "handful" the user cares about */}
      {(w.active_followup || []).length > 0 && (
        <Card className="p-5 rounded-2xl bg-white border mb-6" style={{ borderColor: C.border }} data-testid="bi-waitlist-active-table">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.rose }}>Active follow-up queue · needs a touch today</div>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>
                <th className="text-left py-1">Client</th>
                <th className="text-left py-1">Clinic</th>
                <th className="text-left py-1">Referral source</th>
                <th className="text-right py-1">Received</th>
                <th className="text-right py-1">Last touch</th>
                <th className="text-right py-1">Biz days stale</th>
              </tr>
            </thead>
            <tbody>
              {(w.active_followup || []).map((row, i) => (
                <tr key={`${row.client_name}-${i}`} className="border-t" style={{ borderColor: C.hair }} data-testid={`bi-waitlist-active-row-${i}`}>
                  <td className="py-1.5" style={{ color: C.ink }}>{row.client_name || '—'}</td>
                  <td className="py-1.5" style={{ color: C.ink2 }}>{row.clinic || '—'}</td>
                  <td className="py-1.5" style={{ color: C.ink2 }}>{row.referral_source || '—'}</td>
                  <td className="py-1.5 text-right" style={{ color: C.soft }}>{row.received_iso ? fmtDate(row.received_iso) : '—'}</td>
                  <td className="py-1.5 text-right" style={{ color: C.soft }}>{row.last_touch ? fmtDate(row.last_touch) : '—'}</td>
                  <td className="py-1.5 text-right font-semibold" style={{ color: row.biz_days_since_touch >= 3 ? C.rose : C.amber }}>{row.biz_days_since_touch}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Access time cards */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-access-referral-eval">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Referral → Eval Date</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Median</div><div className="font-serif text-2xl mt-1" style={{ color: C.ink }}>{dayLabel(a.referral_to_eval_days?.median)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Mean</div><div className="font-serif text-2xl mt-1" style={{ color: C.ink }}>{dayLabel(a.referral_to_eval_days?.mean)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>P90</div><div className="font-serif text-2xl mt-1" style={{ color: a.referral_to_eval_days?.p90 > 30 ? C.rose : C.ink }}>{dayLabel(a.referral_to_eval_days?.p90)}</div></div>
          </div>
          <div className="text-[11px] mt-2" style={{ color: C.soft }}>n = {a.referral_to_eval_days?.n || 0}</div>
        </Card>
        <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-access-referral-therapy">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Referral → Therapy Start</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Median</div><div className="font-serif text-2xl mt-1" style={{ color: C.ink }}>{dayLabel(a.referral_to_therapy_start_days?.median)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>Mean</div><div className="font-serif text-2xl mt-1" style={{ color: C.ink }}>{dayLabel(a.referral_to_therapy_start_days?.mean)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}>P90</div><div className="font-serif text-2xl mt-1" style={{ color: a.referral_to_therapy_start_days?.p90 > 60 ? C.rose : C.ink }}>{dayLabel(a.referral_to_therapy_start_days?.p90)}</div></div>
          </div>
          <div className="text-[11px] mt-2" style={{ color: C.soft }}>n = {a.referral_to_therapy_start_days?.n || 0}</div>
        </Card>
      </div>

      {/* New-lead age buckets (only counts disposition=new; contacted is measured by biz-days-since-touch above) */}
      <Card className="p-5 rounded-2xl bg-white border mb-6" style={{ borderColor: C.border }} data-testid="bi-waitlist-age-buckets">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>New (untouched) leads · by age</div>
        <div className="space-y-2">
          {(w.buckets || []).map(b => (
            <div key={b.label} className="flex items-center gap-3 text-[12px]" data-testid={`bi-waitlist-bucket-${b.label.replace(/\W+/g,'-').toLowerCase()}`}>
              <div className="w-24 text-right" style={{ color: C.ink2 }}>{b.label}</div>
              <div className="flex-1 h-5 rounded-md relative" style={{ background: '#F1EDF5' }}>
                <div className="h-full rounded-md" style={{
                  width: `${(b.count / bucketMax) * 100}%`,
                  background: b.label.includes('90+') || b.label.includes('61') ? C.rose
                    : b.label.includes('31') || b.label.includes('15') ? C.amber
                    : C.teal,
                }} />
              </div>
              <div className="w-16 text-right font-serif" style={{ color: C.ink }}>{NUM(b.count)}</div>
            </div>
          ))}
        </div>
      </Card>
      {/* By-clinic + by-source tables — scoped to new (untouched) leads */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-waitlist-by-clinic">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>New leads by clinic</div>
          <table className="w-full text-[12px]">
            <thead><tr className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}><th className="text-left py-1">Clinic</th><th className="text-right py-1">Waiting</th></tr></thead>
            <tbody>{(w.by_clinic || []).map(row => (
              <tr key={row.clinic} className="border-t" style={{ borderColor: C.hair }}>
                <td className="py-1.5" style={{ color: C.ink2 }}>{row.clinic}</td>
                <td className="py-1.5 text-right font-serif" style={{ color: C.ink }}>{NUM(row.count)}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
        <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-waitlist-by-source">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>Top referral sources · new leads</div>
          <table className="w-full text-[12px]">
            <thead><tr className="text-[10px] uppercase tracking-widest" style={{ color: C.soft }}><th className="text-left py-1">Source</th><th className="text-right py-1">Waiting</th></tr></thead>
            <tbody>{(w.by_source || []).map(row => (
              <tr key={row.source} className="border-t" style={{ borderColor: C.hair }}>
                <td className="py-1.5" style={{ color: C.ink2 }}>{row.source}</td>
                <td className="py-1.5 text-right font-serif" style={{ color: C.ink }}>{NUM(row.count)}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      </div>
    </>
  );
}

function ReferralsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clinicFilter, setClinicFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [range, setRange] = useState({ from: null, to: null });

  // Fetch on any filter change. Backend does the heavy lifting so the
  // chart data always matches the summary tiles.
  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (clinicFilter && clinicFilter !== 'all') params.set('clinic', clinicFilter);
    if (bucketFilter && bucketFilter !== 'all') params.set('bucket', bucketFilter);
    if (range.from) params.set('start_date', range.from);
    if (range.to) params.set('end_date', range.to);
    const qs = params.toString();
    api.get(`/inquiries/report/referral-sources${qs ? '?' + qs : ''}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load referral report'))
      .finally(() => setLoading(false));
  }, [clinicFilter, bucketFilter, range.from, range.to]);

  useEffect(() => { loadData(); }, [loadData]);

  // Separate "all clinics" list so the Clinic dropdown stays stable even
  // when a bucket filter blanks a clinic out of the current result set.
  const [allClinics, setAllClinics] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/inquiries/report/referral-sources')
      .then(r => { if (alive) setAllClinics((r.data?.clinics || []).map(c => c.clinic)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const clinics = data?.clinics || [];
  const buckets = data?.buckets || ['Google', 'Website', 'Fax', 'Other'];
  const totals = data?.totals || {};
  const otherGlobal = data?.other_breakdown_global || [];
  const totalLeads = Object.values(totals).reduce((n, v) => n + v, 0);

  // Chart data — one row per clinic, each bucket as a stacked series.
  const chartData = clinics.map(c => {
    const row = { clinic: c.clinic };
    buckets.forEach(b => { row[b] = c.buckets?.[b] || 0; });
    return row;
  });

  const filterActive = clinicFilter !== 'all' || bucketFilter !== 'all' || range.from || range.to;
  const filterSummary = [
    clinicFilter !== 'all' ? `Clinic: ${clinicFilter}` : null,
    bucketFilter !== 'all' ? `Channel: ${bucketFilter}` : null,
    (range.from || range.to) ? `Dates: ${range.from ? fmtDateLong(range.from) : '…'} → ${range.to ? fmtDateLong(range.to) : '…'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      {/* Waitlist & Time-to-Access — reads Inquiries + Clients directly */}
      <WaitlistSection />
      {/* ── Filters ────────────────────────────────────────────────── */}
      <Card className="p-4 rounded-2xl mb-5 bg-white border" style={{ borderColor: C.border }} data-testid="bi-ref-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Filters</div>
          <select
            value={clinicFilter}
            onChange={e => setClinicFilter(e.target.value)}
            data-testid="bi-ref-filter-clinic"
            className="h-9 px-3 rounded-full border text-[13px] bg-white"
            style={{ borderColor: C.border, color: C.ink }}
          >
            <option value="all">All clinics</option>
            {allClinics.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={bucketFilter}
            onChange={e => setBucketFilter(e.target.value)}
            data-testid="bi-ref-filter-bucket"
            className="h-9 px-3 rounded-full border text-[13px] bg-white"
            style={{ borderColor: C.border, color: C.ink }}
          >
            <option value="all">All channels</option>
            {['Google', 'Website', 'Fax', 'Other'].map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <DateRangePicker value={range} onChange={setRange} testid="bi-ref-date" />
          {filterActive && (
            <>
              <div className="text-[11px]" style={{ color: C.soft }}>{filterSummary}</div>
              <Button
                variant="outline"
                onClick={() => { setClinicFilter('all'); setBucketFilter('all'); setRange({ from: null, to: null }); }}
                className="h-9 rounded-full text-[12px]"
                data-testid="bi-ref-filter-clear"
              >
                Clear
              </Button>
            </>
          )}
        </div>
      </Card>

      {loading && <div className="p-8 text-center" style={{ color: C.soft }}>Loading referral intelligence…</div>}

      {!loading && !clinics.length && (
        <Card className="p-12 rounded-3xl text-center bg-white border" style={{ borderColor: C.border }} data-testid="bi-referrals-empty">
          <Inbox size={22} className="mx-auto mb-3" style={{ color: C.purple }} />
          <div className="font-serif text-xl" style={{ color: C.ink }}>No referral data for this view</div>
          <div className="text-sm mt-2" style={{ color: C.soft }}>
            {filterActive
              ? 'Try widening the filters — no leads match the current selection.'
              : 'Inquiries auto-sync from the website Google Sheet every 30 minutes, plus any fax referrals logged manually.'}
          </div>
        </Card>
      )}

      {!loading && clinics.length > 0 && (
        <>
          {/* ── Summary tiles ────────────────────────────────────────── */}
          <div className="grid lg:grid-cols-5 gap-3 mb-6">
            <StatTile label="Total Leads" value={NUM(totalLeads)} sub="Across all channels" icon={Inbox} tone={C.purple} testid="bi-ref-total" />
            {buckets.map(b => (
              <StatTile
                key={b}
                label={b}
                value={NUM(totals[b] || 0)}
                sub={totalLeads ? `${((totals[b] || 0) * 100 / totalLeads).toFixed(1)}% of leads` : '—'}
                icon={b === 'Google' ? Trophy : b === 'Website' ? Activity : b === 'Fax' ? Inbox : Users}
                tone={BUCKET_COLORS[b]}
                testid={`bi-ref-tile-${b.toLowerCase()}`}
              />
            ))}
          </div>

          {/* ── Stacked bar by clinic × channel ─────────────────────── */}
          <SectionTitle title="Referral channels by clinic" subtitle="Google · Website · Fax · Other — which clinics win leads where" />
          <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="bi-ref-chart">
            <ResponsiveContainer width="100%" height={Math.max(340, chartData.length * 38)}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 24, left: 8, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.hair} horizontal={false} />
                <XAxis type="number" stroke={C.soft} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis dataKey="clinic" type="category" stroke={C.soft} fontSize={11} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(31,26,44,0.08)' }} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                {buckets.map((b, i) => (
                  <Bar
                    key={b}
                    dataKey={b}
                    stackId="channel"
                    fill={BUCKET_COLORS[b]}
                    radius={i === buckets.length - 1 ? [0, 6, 6, 0] : 0}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* ── Per-clinic detail table (4 bucket columns + total) ──── */}
          <SectionTitle title="Full channel matrix" subtitle="Rows = clinics · counts pooled from inquiries queue + client roster" />
          <Card className="rounded-2xl overflow-hidden bg-white border mb-6" style={{ borderColor: C.border }} data-testid="bi-ref-table">
            <div className="overflow-auto max-h-[520px]">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider sticky top-0 z-10" style={{ background: C.paper, color: C.soft }}>
                  <tr>
                    <th className="text-left py-3 px-4">Clinic</th>
                    <th className="text-right py-3 px-4">Total</th>
                    {buckets.map(b => (
                      <th key={b} className="text-right py-3 px-3 whitespace-nowrap" style={{ color: BUCKET_COLORS[b] }}>{b}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clinics.map((c) => (
                    <tr key={c.clinic} className="border-t" style={{ borderColor: C.hair }} data-testid={`bi-ref-row-${c.clinic.replace(/\s+/g,'-').toLowerCase()}`}>
                      <td className="py-3 px-4 font-medium" style={{ color: C.ink }}>{c.clinic}</td>
                      <td className="py-3 px-4 text-right font-serif" style={{ color: C.purple }}>{NUM(c.total)}</td>
                      {buckets.map(b => {
                        const n = c.buckets?.[b] || 0;
                        return (
                          <td key={b} className="py-3 px-3 text-right tabular-nums" style={{ color: n ? C.ink2 : C.soft }}>
                            {n || '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── "Other" drilldown — actual sources, not buried ──────── */}
          {(otherGlobal.length > 0) && (
            <>
              <SectionTitle title={`"Other" channel breakdown${clinicFilter !== 'all' ? ` — ${clinicFilter}` : ''}`}
                            subtitle="Every source folded into Other, with per-clinic detail so nothing gets buried" />
              <div className="grid lg:grid-cols-2 gap-4 mb-6">
                <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-ref-other-global">
                  <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: C.soft }}>
                    {clinicFilter === 'all' ? 'All clinics · aggregated' : `${clinicFilter} only`}
                  </div>
                  <ul className="space-y-2">
                    {otherGlobal.map(({ source, count }) => (
                      <li key={source} className="flex items-center justify-between border-b pb-2" style={{ borderColor: C.hair }}
                          data-testid={`bi-ref-other-src-${source.replace(/\s+/g,'-').toLowerCase().slice(0,40)}`}>
                        <span className="text-[13px]" style={{ color: C.ink }}>{source}</span>
                        <span className="font-serif text-[15px] tabular-nums" style={{ color: C.rose }}>{NUM(count)}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card className="p-5 rounded-2xl bg-white border" style={{ borderColor: C.border }} data-testid="bi-ref-other-per-clinic">
                  <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: C.soft }}>By clinic</div>
                  <div className="max-h-[420px] overflow-auto space-y-3">
                    {clinics.filter(c => (c.other_breakdown || []).length).map(c => (
                      <div key={c.clinic}>
                        <div className="text-[12px] font-semibold mb-1" style={{ color: C.ink }}>{c.clinic}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {c.other_breakdown.map(({ source, count }) => (
                            <span key={source}
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
                                  style={{ borderColor: C.hair, background: C.paper, color: C.ink2 }}>
                              {source}
                              <span className="font-serif tabular-nums" style={{ color: C.rose }}>{count}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    {!clinics.some(c => (c.other_breakdown || []).length) && (
                      <div className="text-[12px]" style={{ color: C.soft }}>No "Other" sources in this view.</div>
                    )}
                  </div>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}


// ─── Root page ───
export default function ExecutiveBI() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState({ from: null, to: null }); // date-range filter
  const [validation, setValidation] = useState(null); // iter 206 — daily-data health
  const [validationOpen, setValidationOpen] = useState(false);

  const load = useCallback(async (r = range) => {
    setLoading(true);
    try {
      const params = {};
      if (r.from) params.date_from = r.from;
      if (r.to)   params.date_to   = r.to;
      const [d, s, v] = await Promise.all([
        api.get('/bi/dashboard', { params }).then((res) => res.data),
        api.get('/bi/status').then((res) => res.data),
        api.get('/admin/validate-daily-data/latest').then((res) => res.data).catch(() => null),
      ]);
      setData(d?.empty ? null : d); setStatus(s);
      if (v) setValidation(v);
    } catch (e) { log.error('Failed to load BI dashboard:', e); toast.error('Failed to load BI data'); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(range); }, [range, load]);

  // Poll a background /bi/refresh batch until it completes, then reload data.
  // Mirrors the Import.jsx auto-refresh polling logic so a large practice's
  // 30-90s compute survives Cloudflare's 100s edge timeout AND surfaces the
  // actual runner error (not a masked "Refresh failed" toast).
  //
  // Tolerates transient network / Cloudflare 5xx errors: individual polls that
  // fail are retried (up to 3 consecutive failures) so one flaky Cloudflare
  // hop can't collapse an otherwise-healthy refresh.
  const pollRefresh = async (bid) => {
    let consecutiveErrors = 0;
    for (let i = 0; i < 150; i++) {
      await new Promise(res => setTimeout(res, 2000));
      let doc;
      try {
        const s = await api.get(`/imports/status/${bid}`, { timeout: 15000 });
        doc = s.data;
        consecutiveErrors = 0;
      } catch (err) {
        consecutiveErrors += 1;
        log.warn(`Poll ${i + 1} failed (${consecutiveErrors}/3):`, err?.message || err);
        if (consecutiveErrors >= 3) {
          throw new Error(err?.response?.data?.detail || err?.message || 'Poll network error');
        }
        continue;
      }
      if (doc?.rows_total && doc?.rows_done != null) {
        setRefreshProgress(`Refreshing BI cube — step ${doc.rows_done} of ${doc.rows_total}`);
      }
      if (doc?.status === 'complete') return true;
      if (doc?.status === 'failed') throw new Error(doc.error || 'Cube refresh failed');
    }
    throw new Error('Cube refresh timed out after 5 minutes. The runner may still be processing — reload in a minute.');
  };

  const refresh = async () => {
    setRefreshing(true);
    setRefreshProgress('Refreshing BI cube — step 1 of 6…');
    try {
      const r = await api.post('/bi/refresh', {}, { timeout: 15000 });
      const bid = r.data?.batch_id;
      if (bid && r.data?.status === 'processing') {
        await pollRefresh(bid);
      }
      // Legacy sync response — nothing else to do.
      toast.success('Cube refreshed');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Refresh failed';
      log.error('Cube refresh failed:', err);
      toast.error(`Refresh failed: ${msg}`);
    } finally {
      setRefreshing(false);
      setRefreshProgress(null);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        try {
          const r = await api.post('/bi/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.success(`${f.name}: ${r.data.type} · ${r.data.rows} rows`);
        } catch (err) {
          toast.error(`${f.name}: ${err?.response?.data?.detail || 'Upload failed'}`);
        }
      }
      // Kick and poll the cube refresh so the UI reflects the fresh data.
      try {
        setRefreshProgress('Refreshing BI cube…');
        const r = await api.post('/bi/refresh', {}, { timeout: 15000 });
        const bid = r.data?.batch_id;
        if (bid && r.data?.status === 'processing') await pollRefresh(bid);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Cube refresh failed';
        log.error('Post-upload cube refresh failed:', err);
        toast.error(`Refresh failed: ${msg}`);
      } finally {
        setRefreshProgress(null);
      }
      // iter 206 — surface the daily-data validation result BEFORE
      // the uploader clicks away. Runs foreground so the banner
      // updates immediately. Best-effort — a validator failure is
      // never allowed to mask a successful upload.
      try {
        const vr = await api.post('/admin/validate-daily-data', {}, { timeout: 30000 });
        if (vr?.data) {
          setValidation(vr.data);
          if (vr.data.status === 'PASS') {
            toast.success('Daily data validation: PASS');
          } else {
            toast.error(`Daily data validation: FAIL (${vr.data.flagged_clinicians?.length || 0} flagged)`);
          }
        }
      } catch (err) {
        log.warn('Validation run failed:', err?.message || err);
      }
      await load();
    } finally { setUploading(false); e.target.value = ''; }
  };

  const isForbidden = user && user.role !== 'super_user';
  const throughDate = data?.through_date ? fmtDate(data.through_date) : '—';

  if (isForbidden) {
    return <div className="p-8 text-center text-[#685B72]" data-testid="bi-forbidden">Executive BI is restricted to Super Users.</div>;
  }

  const tabs = [
    { id: 'overview',    label: 'Executive Overview',              icon: LineIcon   },
    { id: 'director',    label: 'Director Monthly Review',          icon: Trophy     },
    { id: 'clinics',     label: 'Locations',                        icon: Building2  },
    { id: 'financial',   label: 'Financial & Revenue Cycle',        icon: DollarSign },
    { id: 'referrals',   label: 'Demand & Access',                  icon: Inbox      },
    { id: 'operations',  label: 'Operations & Capacity',            icon: Activity   },
    { id: 'clinicians',  label: 'Workforce',                        icon: Users      },
  ];

  return (
    <div data-testid="executive-bi" style={{ background: C.paper, minHeight: 'calc(100vh - 4rem)', margin: '-1rem -1rem -2rem -1rem', padding: '1.5rem 2rem 2.5rem' }}>
      {/* Page header — clean, single row */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: C.gold }}>
            <Crown size={12} /> Executive Command Center
          </div>
          <h1 className="font-serif mt-1.5 leading-none" style={{ color: C.ink, fontSize: 'clamp(28px, 4vw, 40px)' }}>Thrive Master BI</h1>
          <div className="text-[12px] mt-2" style={{ color: C.soft }}>
            YTD through <span className="font-semibold" style={{ color: C.ink2 }}>{throughDate}</span>
            {data?.months?.length ? ` · ${data.months.length} active month${data.months.length === 1 ? '' : 's'}` : ''}
          </div>
          {/* Data freshness — surfaces per-collection latest DOS so users can
              tell WHICH file is stale (e.g. Attendance through Jul 22 but
              Appt Status through Jun 30 = revenue KPIs need re-upload). */}
          {data?.data_freshness && (
            <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]" data-testid="bi-data-freshness">
              {['attendance', 'appt_status', 'claims'].map(k => {
                const f = data.data_freshness[k];
                if (!f || !f.latest_dos) return null;
                const latest = new Date(f.latest_dos);
                const now = new Date();
                const days = Math.floor((now - latest) / 86400000);
                const stale = days > 10;
                return (
                  <span key={k} className="px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest border"
                        style={{
                          background: stale ? '#F5EAEA' : '#E5EFEC',
                          color:      stale ? '#8A3A3A' : '#839E97',
                          borderColor: stale ? '#8A3A3A55' : '#839E9744',
                        }}
                        title={`Latest DOS: ${f.latest_dos} · ${f.rows.toLocaleString()} rows`}>
                    {k.replace('_', ' ')} · {fmtDate(f.latest_dos)}
                    {stale && ' · stale'}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <DateRangePicker value={range} onChange={setRange} testid="bi-date-range" />
          <Button onClick={refresh} disabled={refreshing || loading} data-testid="bi-refresh-btn"
                  className="rounded-full h-10 px-5 border-0" style={{ background: C.ink, color: '#FFFFFF' }}>
            <RefreshCcw size={14} className={refreshing ? 'animate-spin mr-2' : 'mr-2'} /> Refresh Cube
          </Button>
        </div>
      </div>

      {refreshProgress && (
        <div className="mb-4 px-4 py-2 rounded-xl text-sm inline-flex items-center gap-2"
             style={{ background: '#F7EFDA', color: C.ink2, border: `1px solid ${C.border}` }}
             data-testid="bi-refresh-progress">
          <RefreshCcw size={12} className="animate-spin" />
          {refreshProgress}
        </div>
      )}

      {/* iter 206 — Daily-data validation banner. Green PASS or red
          FAIL surfaces immediately after any SP upload completes so
          the uploader sees the health check before anyone else looks
          at the cube. Click the banner to expand check-by-check
          details + flagged clinicians. */}
      {validation && validation.status && !validation.never_run && (
        <div className="mb-4"
             data-testid={`bi-validation-banner-${(validation.status || '').toLowerCase()}`}>
          <button
            type="button"
            onClick={() => setValidationOpen((v) => !v)}
            className="w-full flex items-start gap-3 px-5 py-3 rounded-2xl border text-left transition hover:opacity-95"
            style={{
              background: validation.status === 'PASS' ? '#EAF2EE' : '#F6E3E3',
              borderColor: validation.status === 'PASS' ? '#839E9755' : '#8A3A3A55',
            }}
            data-testid="bi-validation-toggle"
          >
            {validation.status === 'PASS'
              ? <CheckCircle2 size={18} style={{ color: '#5B7A72', marginTop: 2, flexShrink: 0 }} />
              : <AlertTriangle size={18} style={{ color: '#8A3A3A', marginTop: 2, flexShrink: 0 }} />
            }
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm"
                   style={{ color: validation.status === 'PASS' ? '#5B7A72' : '#8A3A3A' }}>
                Daily data validation: {validation.status}
                {validation.flagged_clinicians?.length ? ` — ${validation.flagged_clinicians.length} clinician(s) flagged` : ''}
              </div>
              <div className="text-xs mt-1"
                   style={{ color: validation.status === 'PASS' ? '#5B7A72' : '#8A3A3A' }}>
                Pay period {validation.period_start} → {validation.period_end} · last run {fmtDateTime(validation.timestamp)}
                {' · '}<span className="underline">{validationOpen ? 'hide details' : 'show details'}</span>
              </div>
            </div>
          </button>

          {validationOpen && (
            <div className="mt-2 rounded-2xl border bg-white p-4 text-xs space-y-3"
                 style={{ borderColor: C.border, color: C.ink2 }}
                 data-testid="bi-validation-details">
              {(validation.checks || []).map((c, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  {c.status === 'PASS'
                    ? <CheckCircle2 size={14} style={{ color: '#5B7A72', marginTop: 2, flexShrink: 0 }} />
                    : <AlertTriangle size={14} style={{ color: '#8A3A3A', marginTop: 2, flexShrink: 0 }} />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="font-medium" style={{ color: C.ink }}>
                      {c.name} — <span style={{ color: c.status === 'PASS' ? '#5B7A72' : '#8A3A3A' }}>{c.status}</span>
                    </div>
                    {c.status === 'FAIL' && c.mismatches?.length > 0 && (
                      <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
                        {c.mismatches.slice(0, 5).map((m, i) => (
                          <div key={i}>· {m.clinician}: perf={m.perf_shows}, bi={m.bi_shows} (missing {m.missing})</div>
                        ))}
                        {c.mismatches.length > 5 && <div>… and {c.mismatches.length - 5} more</div>}
                      </div>
                    )}
                    {c.status === 'FAIL' && c.duplicates?.length > 0 && (
                      <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
                        {c.duplicates.slice(0, 5).map((d, i) => (
                          <div key={i}>· {d.client} / {d.clinician} / {d.dos} — {d.count}× copies</div>
                        ))}
                        {c.duplicates.length > 5 && <div>… and {c.duplicates.length - 5} more groups</div>}
                      </div>
                    )}
                    {c.status === 'FAIL' && c.outliers?.length > 0 && (
                      <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
                        {c.outliers.slice(0, 5).map((o, i) => (
                          <div key={i}>· {o.clinician}: 92507={o.cpt_92507_count}, Show={o.show_count} (+{o.overshoot_pct}% over)</div>
                        ))}
                        {c.outliers.length > 5 && <div>… and {c.outliers.length - 5} more</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* iter 71 — Stale-parse detector: appt_status has rows but $0 was
          collected AND every clinic has $0 collected. This is the fingerprint
          of data ingested by the pre-iter-69 parser (which silently dropped
          insurance payments). Users can't diagnose this on their own, so
          surface a proactive re-upload nudge here. */}
      {data && data.data_freshness?.appt_status?.rows > 100
          && (data.kpis?.collected_dos || 0) === 0
          && (data.clinic_scorecard || []).every(c => (c.collected || 0) === 0) && (
        <div className="mb-4 px-5 py-4 rounded-2xl border flex flex-wrap items-start gap-3"
             style={{ background: '#F7EFDA', borderColor: '#B08A3A66' }}
             data-testid="bi-stale-data-banner">
          <AlertTriangle size={18} style={{ color: '#B08A3A', marginTop: 2, flexShrink: 0 }} />
          <div className="flex-1 min-w-[280px]">
            <div className="font-semibold text-sm" style={{ color: '#B08A3A' }}>
              Revenue data looks stale — please re-upload the SimplePractice appointment-status report
            </div>
            <div className="text-xs mt-1" style={{ color: '#B08A3A' }}>
              We shipped a fix that correctly reads both the client-paid and insurance-paid columns.
              Your current cube still has data from before that fix (all $0 collected). Click <b>Upload SP Reports</b> above,
              re-select <code style={{ background: '#F7EFDA', padding: '1px 5px', borderRadius: 4 }}>appointment-status-report.xlsx</code>, and this dashboard will repopulate automatically.
            </div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="mb-6 max-w-full overflow-x-auto"><div className="inline-flex items-center gap-1 p-1 rounded-full whitespace-nowrap" style={{ background: '#F2EDE5', border: `1px solid ${C.border}` }}>
        {tabs.map((t) => <Tab key={t.id} {...t} active={tab === t.id} onClick={setTab} />)}
      </div></div>

      {loading && <div className="p-8 text-center" style={{ color: C.soft }}>Loading executive metrics…</div>}

      {/* Referrals tab pulls its own data — renders even when BI cube is empty */}
      {!loading && tab === 'referrals' && <ReferralsTab />}
      {!loading && tab === 'director'  && <DirectorMonthlyReview />}

      {!loading && !data && tab !== 'referrals' && tab !== 'director' && (
        <Card className="p-12 rounded-3xl text-center bg-white border" data-testid="bi-empty" style={{ borderColor: C.border }}>
          <Sparkles size={22} className="mx-auto mb-3" style={{ color: C.purple }} />
          <div className="font-serif text-xl" style={{ color: C.ink }}>No BI data yet</div>
          <div className="text-sm mt-2" style={{ color: C.soft }}>Upload the monthly SimplePractice exports above and hit Refresh Cube.</div>
        </Card>
      )}

      {!loading && data && tab !== 'referrals' && tab !== 'director' && (
        <>
          {tab === 'overview'    && (<><OverviewTab d={data} /><MonthlyTablesSection showUnderTarget /></>)}
          {tab === 'financial'   && (<><FinancialTab   d={data} /><PayersTab d={data} /></>)}
          {tab === 'operations'  && <OperationsTab  d={data} />}
          {tab === 'clinicians'  && <CliniciansTab  d={data} />}
          {tab === 'clinics'     && <ClinicsTab     d={data} />}

          <div className="text-center text-[10px] uppercase tracking-widest mt-8" style={{ color: C.soft }}>
            Last refresh: {fmtDateTime(data.generated_at)} · Data through {throughDate}
          </div>
        </>
      )}
    </div>
  );
}
